import {
  Box,
  useTheme,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Autocomplete,
  createFilterOptions,
  InputAdornment,
  Switch,
  Paper,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { getRequestorInvoiceDefaults } from "helpers/requestorGroupHelpers";
import {
  buildEmailThreadLink,
  inspectEmailThreadInput,
  parseEmailThreadIds,
} from "helpers/ServiceHelpers";
import CEDatePicker from "components/CEDatePicker";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Overlay from "components/Overlay";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import utc from "dayjs/plugin/utc";
import { HtmlTooltip } from "components/HeatMap";
import { CustomPopper } from "components/CustomPopper";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxIcon from "@mui/icons-material/CheckBoxRounded";
import { useAuth } from "../../../contexts/AuthContext";
import { getBorderColor } from "../interpretations/InterpretationNewEdit";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePresence } from "contexts/PresenceContext";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useNavigate } from "react-router-dom";

const EMPTY_LINGUIST_CLIENT_STATS = {
  completedAssignments: 0,
  cancelledUnableToFillAssignments: 0,
};

const EMPTY_LANGUAGE_CLIENT_STATS = {
  requestCount: 0,
};

const LONG_PRESS_AUTOFILL_MS = 900;
const LOREM_NAME_SEEDS = [
  "Lorem ipsum",
  "Dolor sit",
  "Amet consectetur",
  "Adipiscing elit",
  "Sed do",
  "Eiusmod tempor",
  "Incididunt ut",
  "Labore magna",
];

// Cost formula helper (2026-10-01, mirrors InterpretationNewEdit): renders the
// calculation breakdown under Account Charge / Total Linguist Pay / Total
// Cost. Every number with a `tip` carries a hover tooltip explaining it.
const CostFormulaHelper = ({ parts }) => {
  if (!parts || parts.length === 0) return null;

  return (
    <>
      {parts.map((part, index) =>
        part.tip ? (
          <Tooltip key={index} title={part.tip} arrow placement="top">
            <Box
              component="span"
              sx={{
                cursor: "help",
                borderBottom: "1px dotted",
                borderColor: "text.disabled",
              }}
            >
              {part.text}
            </Box>
          </Tooltip>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
};

const TranslationNewEdit = () => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { currentEntity, setCurrentEntity } = usePresence();

  const filter = createFilterOptions();

  const theme = useTheme();
  const optionSwitchSx = {
    ml: 0.5,
    mr: 0.25,
    "& .MuiSwitch-track": {
      backgroundColor: theme.palette.common.white,
      opacity: "1 !important",
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: `${theme.palette.primary.main}40 !important`,
      borderColor: "transparent",
      opacity: "1 !important",
    },
    "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb": {
      backgroundColor: theme.palette.grey[400],
    },
  };
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  
  const {
    overlayData,
    setOverlayData,
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    closeDialog,
  } = useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.TRANSLATION_ACTION_NEW
      ? "New Translation"
      : "Edit Translation",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  // State for paid assignment warning dialog
  const [showPaidWarning, setShowPaidWarning] = useState(false);
  const [paidWarningMessage, setPaidWarningMessage] = useState("");
  const [pendingSave, setPendingSave] = useState(false);

  const isNewAssignment =
    overlayData.Action === OVERLAY_NAMES.TRANSLATION_ACTION_NEW;
  const prefilledData = useMemo(
    () => (isNewAssignment ? overlayData?.PrefilledData || {} : {}),
    [isNewAssignment, overlayData?.PrefilledData],
  );
  const isAmineUser = (currentUser?.Name || "").trim() === "Amine";

  // Paid-invoice lock: a service on a PAID invoice is frozen — Save is
  // disabled and a red message shows instead of the confirm-dialog dance.
  const paidLocked =
    !isNewAssignment && Boolean(overlayData?.Invoice?.PaymentReceivedDate);

  // Freeze ALL fields too (not just Save): force isEditable off while the
  // service sits on a paid invoice. Runs whenever either changes, so no
  // error-recovery setIsEditable(true) can re-enable a paid-locked form.
  useEffect(() => {
    if (paidLocked) setIsEditable(false);
  }, [paidLocked, isEditable]);

  // Edit lock for existing services only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "service",
    isNewAssignment ? null : overlayData.id,
  );

  // Snapshot the entity the underlying view (list/profile) had before this
  // overlay took over, so closing the overlay can restore it instead of
  // nulling it — nulling would drop the still-mounted list/profile out of its
  // entity room and it would stop receiving live updates.
  const previousEntityRef = useRef(null);
  const hasTakenOverEntityRef = useRef(false);

  // Set entity tracking for presence and updates
  useEffect(() => {
    if (!isNewAssignment && overlayData.id) {
      if (!hasTakenOverEntityRef.current) {
        hasTakenOverEntityRef.current = true;
        // Snapshot only on first takeover: currentEntity is intentionally NOT
        // a dependency, so the closure keeps the underlying view's entity from
        // the render that created this effect.
        previousEntityRef.current = currentEntity;
      }
      setCurrentEntity({
        entityType: "Translation",
        entityId: overlayData.id,
      });
    }
    return () => {
      if (hasTakenOverEntityRef.current) {
        // Restore the underlying view's entity (keeps its room alive) instead
        // of clearing it; fall back to clearing when there was nothing to
        // restore.
        setCurrentEntity(
          previousEntityRef.current || { entityType: null, entityId: null },
        );
        hasTakenOverEntityRef.current = false;
        previousEntityRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentEntity intentionally excluded: the snapshot must keep the pre-takeover entity from the render that created this effect (see comment above)
  }, [isNewAssignment, overlayData.id, setCurrentEntity]);

  // Tracks whether the user has unsaved form edits. Kept in a ref (declared
  // before the state it mirrors, since `hasChanges` is defined later in the
  // component body) so the socket refetch handler can check it synchronously.
  const hasChangesRef = useRef(false);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = () => {
    // If the user has unsaved edits, don't clobber their form with the remote
    // snapshot. Refreshing overlayData here would reset the dirty baseline
    // (initialDataRef syncs from overlayData) and silently discard their work.
    // Keep the list/profile behind the overlay fresh and warn instead.
    if (hasChangesRef.current) {
      triggerPageRefresh();
      showSnackbar(
        "This assignment was updated by another member. Review your changes before saving.",
        theme,
        "info",
        theme.palette.warning.main,
      );
      return;
    }
    // Reload the overlay data with fresh data from server
    api
      .get(`/services/translations/${overlayData.id}`)
      .then((response) => {
        setOverlayData({
          ...overlayData,
          ...response.data,
        });
        // Refresh the list behind the overlay, but skip the success snackbar —
        // form fields are local state and don't change on remote updates, so
        // "Assignment data refreshed" would be misleading.
        triggerPageRefresh();
      })
      .catch((err) => console.log("Error refetching assignment:", err));
  };

  useAssignmentUpdates({
    modelType: "Translation",
    recordId: isNewAssignment ? null : overlayData.id,
    onUpdate: handleAssignmentRefetch,
  });

  const [, setAddNewClient] = useState(false);
  const [, setAddNewRequestor] = useState(false);
  const [, setAddNewInvoiceTo] = useState(false);

  const completing =
    overlayData.Action === OVERLAY_NAMES.TRANSLATION_ACTION_COMPLETE;

  const [name, setName] = useState(
    isNewAssignment ? prefilledData.Name || "" : overlayData.Name,
  );

  const [date, setDate] = useState(
    isNewAssignment
      ? prefilledData.Date
        ? dayjs(prefilledData.Date)
        : dayjs()
      : dayjs(overlayData.Date),
  );

  const [deadline, setDeadline] = useState(
    isNewAssignment
      ? prefilledData.Deadline
        ? dayjs(prefilledData.Deadline)
        : null
      : dayjs(overlayData.Deadline),
  );

  const [deliveryDate, setDeliveryDate] = useState(
    isNewAssignment
      ? prefilledData.DeliveryDate
        ? dayjs(prefilledData.DeliveryDate)
        : null
      : dayjs(overlayData.DeliveryDate),
  );

  const [projectNumber, setProjectNumber] = useState(
    overlayData.ProjectNumber || "",
  );

  useEffect(() => {
    if (!isNewAssignment) return;

    let isMounted = true;

    api
      .get("/services/translations/latestProjectNumber")
      .then((response) => {
        if (!isMounted) return;
        const nextProjectNumber =
          Number(response?.data?.ProjectNumber || 0) + 1;
        setProjectNumber(String(nextProjectNumber));
        setProjectNumberExists(false);
      })
      .catch((error) => {
        console.error(
          "[TranslationNewEdit] Error fetching latest project number:",
          error,
        );
      });

    return () => {
      isMounted = false;
    };
  }, [isNewAssignment]);

  const [translationType, setTranslationType] = useState(
    isNewAssignment
      ? prefilledData.TranslationType || "Translation"
      : overlayData.ServiceSubtype?.Name,
  );
  const [translationSubtypeOptions, setTranslationSubtypeOptions] = useState([]);

  const resolveTranslationSubtypeId = useCallback(
    (typeName) => {
      const normalized = String(typeName || "").trim().toLowerCase();
      if (!normalized) return null;
      const match = translationSubtypeOptions.find(
        (opt) => String(opt?.Name || "").trim().toLowerCase() === normalized,
      );
      const parsed = Number(match?.id);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    },
    [translationSubtypeOptions],
  );

  const [client, setClient] = useState(
    isNewAssignment ? prefilledData.Client || "" : overlayData.Client,
  );
  const [languageClientStatsById, setLanguageClientStatsById] = useState({});

  const [requestor, setRequestor] = useState(
    isNewAssignment ? prefilledData.Requestor || "" : overlayData.Requestor,
  );

  const [invoiceTo, setInvoiceTo] = useState(
    isNewAssignment ? prefilledData.Invoicer || "" : overlayData.Invoicer,
  );

  // CC recipients resolved from the requestor's group (read-only display;
  // not persisted on the service).
  const [ccRequestors, setCcRequestors] = useState([]);
  const applyRequestorInvoiceDefaults = useCallback((requestorValue) => {
    const { invoiceTo: nextInvoiceTo, ccRequestors: nextCcRequestors } =
      getRequestorInvoiceDefaults(requestorValue);
    setInvoiceTo(nextInvoiceTo || "");
    setCcRequestors(nextCcRequestors);
  }, []);

  const [sourceLanguage, setSourceLanguage] = useState(
    isNewAssignment
      ? prefilledData.SourceLanguage ||
        overlayData.Languages?.find((l) => l.Name === "English") ||
        null
      : overlayData.Language,
  );

  const assignmentTargetLanguages =
    overlayData.ServiceTargetLanguages &&
    overlayData.ServiceTargetLanguages.map((language) => ({
      id: language.LanguageID,
      Name: language.Language.Name,
    }));

  const [targetLanguages, setTargetLanguages] = useState(
    isNewAssignment
      ? prefilledData.TargetLanguages || []
      : assignmentTargetLanguages || [],
  );

  const [unitTypes, setUnitTypes] = useState([]);

  const [unitTypeId, setUnitTypeId] = useState(
    isNewAssignment
      ? prefilledData.UnitTypeID || ""
      : overlayData.UnitTypeID || overlayData.UnitType?.id || "",
  );

  const [unitCount, setUnitCount] = useState(
    isNewAssignment
      ? (prefilledData.UnitCount ?? prefilledData.WordCount ?? "")
      : (overlayData.UnitCount ?? overlayData.WordCount),
  );

  // Unit Rate (2026-10-05): older translations stored their effective rate in
  // the legacy `Rate` column (UnitRate came later — ~1,700 rows are NULL).
  // Now that the Rate FIELD is gone, display the stored Unit Rate or fall
  // back to the legacy Rate so the field is never blank on edit.
  const [unitRate, setUnitRate] = useState(
    isNewAssignment
      ? (prefilledData.UnitRate ?? prefilledData.Rate ?? "")
      : (overlayData.UnitRate ?? overlayData.Rate ?? ""),
  );

  useEffect(() => {
    let isMounted = true;

    api
      .get("/unitTypes")
      .then((response) => {
        if (!isMounted) return;
        setUnitTypes(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching unit types:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    api
      .get("/services/translations/types/options")
      .then((response) => {
        if (!isMounted) return;
        setTranslationSubtypeOptions(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Error fetching translation subtype options:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!unitTypes.length) return;

    if (unitTypeId) return;

    if (!isNewAssignment && overlayData.UnitTypeID) {
      setUnitTypeId(overlayData.UnitTypeID);
      return;
    }

    const defaultUnit = unitTypes.find((unit) => unit.Name === "Word");
    if (defaultUnit) {
      setUnitTypeId(defaultUnit.id);
    } else if (unitTypes[0]) {
      setUnitTypeId(unitTypes[0].id);
    }
  }, [unitTypes, unitTypeId, isNewAssignment, overlayData.UnitTypeID]);

  const selectedUnitType = unitTypes.find(
    (unit) => unit.id === Number(unitTypeId),
  );
  const unitTypeName = selectedUnitType?.Name?.toLowerCase() || "unit";
  const unitTypeAbbrev = selectedUnitType?.Abbreviation || unitTypeName;
  const translationUnitTypes = unitTypes.filter((unit) => unit.Name !== "Hour");

  const [numberOfLanguages, setNumberOfLanguages] = useState(
    isNewAssignment
      ? prefilledData.NumberOfLanguages || prefilledData.TargetLanguages?.length || 1
      : overlayData.NumberOfLanguages || targetLanguages.length,
  );

  const englishLanguage = overlayData.Languages?.find(
    (language) => language.Name === "English",
  );

  const getLanguageKey = (language) => {
    if (!language) return "";

    if (language.id != null) {
      return `id:${language.id}`;
    }

    return `name:${(language.Name || "").trim().toLowerCase()}`;
  };

  const isEnglishLanguage = (language) =>
    (language?.Name || "").trim().toLowerCase() === "english";

  const normalizeTargetLanguages = useCallback((languages, selectedSourceLanguage) => {
    const sourceKey = getLanguageKey(selectedSourceLanguage);
    const uniqueLanguages = [];
    const seenKeys = new Set();

    (languages || []).forEach((language) => {
      const languageKey = getLanguageKey(language);

      if (
        !languageKey ||
        languageKey === sourceKey ||
        seenKeys.has(languageKey)
      ) {
        return;
      }

      seenKeys.add(languageKey);
      uniqueLanguages.push(language);
    });

    if (
      selectedSourceLanguage &&
      !isEnglishLanguage(selectedSourceLanguage) &&
      englishLanguage
    ) {
      const englishKey = getLanguageKey(englishLanguage);

      if (!seenKeys.has(englishKey)) {
        uniqueLanguages.unshift({
          id: englishLanguage.id,
          Name: englishLanguage.Name,
        });
      }
    }

    return uniqueLanguages;
  }, [englishLanguage]);

  const [deliveryType, setDeliveryType] = useState(
    isNewAssignment ? prefilledData.DeliveryType || "" : overlayData.DeliveryType,
  );

  const [rateType, setRateType] = useState(
    isNewAssignment
      ? prefilledData.RateType || "Translation"
      : overlayData.RateType || "Translation",
  );

  const [paymentType, setPaymentType] = useState(
    isNewAssignment ? prefilledData.PaymentType || "" : overlayData.PaymentType,
  );

  const [notarizationOrCOTCost, setNotarizationOrCOTCost] = useState(
    isNewAssignment
      ? prefilledData.NotarizationOrCOTCost || ""
      : overlayData.NotarizationOrCOTCost,
  );

  const [paymentFees, setPaymentFees] = useState(
    isNewAssignment ? prefilledData.PaymentFees || "" : overlayData.PaymentFees,
  );

  const [deliveryCost, setDeliveryCost] = useState(
    isNewAssignment ? prefilledData.DeliveryCost || "" : overlayData.DeliveryCost,
  );

  const [linguist, setLinguist] = useState(
    isNewAssignment
      ? prefilledData.Linguist || overlayData.Linguists?.find((l) => l.Name === "SmartCat") || {
          Name: "N/A",
        }
      : overlayData.Linguist,
  );
  const [linguistClientStatsById, setLinguistClientStatsById] = useState({});
  const hasSelectedClient = Number(client?.id || 0) > 0;

  const languageOptions = useMemo(() => {
    // "ALL" is the rate pseudo-language (used by client default rates) — it
    // must never appear in service language pickers.
    const languages = [...(overlayData.Languages || [])].filter(
      (language) =>
        String(language?.Name || "").trim().toUpperCase() !== "ALL",
    );

    if (!hasSelectedClient) {
      return languages;
    }

    return languages.sort((left, right) => {
      const requestDelta =
        (languageClientStatsById[Number(right?.id || 0)]?.requestCount || 0) -
        (languageClientStatsById[Number(left?.id || 0)]?.requestCount || 0);

      if (requestDelta !== 0) return requestDelta;
      return String(left?.Name || "").localeCompare(String(right?.Name || ""));
    });
  }, [hasSelectedClient, languageClientStatsById, overlayData.Languages]);

  const renderTranslationLanguageOption = useCallback(
    (props, option) => {
      if (option?.inputValue || !option?.id || !hasSelectedClient) {
        return (
          <Box component="li" {...props}>
            {option?.Name || ""}
          </Box>
        );
      }

      const stats =
        languageClientStatsById[Number(option?.id || 0)] ||
        EMPTY_LANGUAGE_CLIENT_STATS;

      return (
        <Box
          component="li"
          {...props}
          sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            {option?.Name || ""}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
          >
            {`Translations: ${stats.requestCount}`}
          </Typography>
        </Box>
      );
    },
    [hasSelectedClient, languageClientStatsById],
  );

  const linguistOptions = useMemo(
    () =>
      [...(overlayData.Linguists || [])]
        .map((linguistOption) => ({
          ...linguistOption,
          ClientServiceStats:
            linguistClientStatsById[Number(linguistOption?.id || 0)] ||
            EMPTY_LINGUIST_CLIENT_STATS,
        }))
        .sort((left, right) => {
          const completedDelta =
            (right?.ClientServiceStats?.completedAssignments || 0) -
            (left?.ClientServiceStats?.completedAssignments || 0);

          if (completedDelta !== 0) return completedDelta;
          return String(left?.Name || "").localeCompare(String(right?.Name || ""));
        }),
    [overlayData.Linguists, linguistClientStatsById],
  );

  const renderLinguistOption = useCallback((props, option) => {
    if (option?.inputValue || !option?.id) {
      return (
        <Box component="li" {...props}>
          {option?.Name || ""}
        </Box>
      );
    }

    const stats = option.ClientServiceStats || EMPTY_LINGUIST_CLIENT_STATS;
    const statsLabel =
      stats.cancelledUnableToFillAssignments > 0
        ? `Completed: ${stats.completedAssignments} | Unable to fill: ${stats.cancelledUnableToFillAssignments}`
        : `Completed: ${stats.completedAssignments}`;

    return (
      <Box
        component="li"
        {...props}
        sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
      >
        <Box component="span" sx={{ minWidth: 0 }}>
          {option?.Name || ""}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
        >
          {statsLabel}
        </Typography>
      </Box>
    );
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const linguistIds = (overlayData.Linguists || [])
      .map((linguistOption) => Number(linguistOption?.id || 0))
      .filter((id) => id > 0);

    if (!clientId || !linguistIds.length) {
      setLinguistClientStatsById({});
      return undefined;
    }

    const fetchLinguistClientStats = async () => {
      try {
        const response = await api.post("/linguists/client-service-stats", {
          clientId,
          linguistIds,
        });

        if (!isCancelled) {
          setLinguistClientStatsById(response.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch linguist client service stats:", error);
        if (!isCancelled) {
          setLinguistClientStatsById({});
        }
      }
    };

    fetchLinguistClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client, overlayData.Linguists]);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const languageIds = (overlayData.Languages || [])
      .map((languageOption) => Number(languageOption?.id || 0))
      .filter((id) => id > 0);

    if (!clientId || !languageIds.length) {
      setLanguageClientStatsById({});
      return undefined;
    }

    const fetchLanguageClientStats = async () => {
      try {
        const response = await api.post("/languages/client-service-request-stats", {
          clientId,
          languageIds,
          serviceCategory: "Translation",
        });

        if (!isCancelled) {
          setLanguageClientStatsById(response.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch language client service stats:", error);
        if (!isCancelled) {
          setLanguageClientStatsById({});
        }
      }
    };

    fetchLanguageClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client, overlayData.Languages]);

  const [translationStatus, setTranslationStatus] = useState(
    isNewAssignment
      ? prefilledData.TranslationStatus || "In-Progress"
      : completing
        ? "Completed"
        : overlayData.TranslationStatus,
  );

  const [accountCharge, setAccountCharge] = useState(
    isNewAssignment ? prefilledData.AccountCharge || "" : overlayData.AccountCharge,
  );

  // 2026-10-06 (a) model ("calculate by default, your entry wins") — manual
  // mode (DisableAutoChargeCalc): a stored non-null, non-zero total is a USER
  // OVERRIDE and wins; null/0 means "calculated" — live sums fill the fields
  // and Save sends null so a computed display never freezes as an override.
  const isManualChargeModeAtInit =
    isNewAssignment ? false : Boolean(overlayData.DisableAutoChargeCalc);
  const storedTotalCostAtInit = isNewAssignment
    ? prefilledData.TotalCost
    : overlayData.TotalCost;
  const storedTotalLinguistPayAtInit = isNewAssignment
    ? prefilledData.TotalLinguistPay
    : overlayData.TotalLinguistPay;
  const hasTotalCostOverrideAtInit =
    storedTotalCostAtInit != null &&
    storedTotalCostAtInit !== "" &&
    Number(storedTotalCostAtInit) !== 0;
  const hasTotalLinguistPayOverrideAtInit =
    storedTotalLinguistPayAtInit != null &&
    storedTotalLinguistPayAtInit !== "" &&
    Number(storedTotalLinguistPayAtInit) !== 0;
  const totalCostOverriddenRef = useRef(hasTotalCostOverrideAtInit);
  const totalLinguistPayOverriddenRef = useRef(
    hasTotalLinguistPayOverrideAtInit,
  );
  // Legacy services with NO cost-line data keep the old stored-TotalCost
  // fallback for Total Linguist Pay (non-null, non-zero only).
  const legacyTotalCostTlPayFallbackRef = useRef(
    hasTotalCostOverrideAtInit ? storedTotalCostAtInit : null,
  );
  // Hydration markers for the (a) model's manual-mode live totals.
  const hasCostLinesRef = useRef(false);
  const costRowsHydratedRef = useRef(false);

  const [totalCost, setTotalCost] = useState(
    isManualChargeModeAtInit
      ? hasTotalCostOverrideAtInit
        ? storedTotalCostAtInit
        : ""
      : isNewAssignment
        ? prefilledData.TotalCost || ""
        : overlayData.TotalCost,
  );

  const [totalLinguistPay, setTotalLinguistPay] = useState(
    isManualChargeModeAtInit
      ? hasTotalLinguistPayOverrideAtInit
        ? storedTotalLinguistPayAtInit
        : ""
      : isNewAssignment
        ? prefilledData.TotalLinguistPay || prefilledData.TotalCost || ""
        : (overlayData.TotalLinguistPay ?? overlayData.TotalCost ?? ""),
  );

  // Calculation-breakdown parts for the three money fields. Each part is
  // { text, tip } — a `tip` renders that number as a hover tooltip naming the
  // value (2026-10-01, mirrors InterpretationNewEdit).
  const [totalCostHelperParts, setTotalCostHelperParts] = useState([]);
  const [accountChargeHelperParts, setAccountChargeHelperParts] = useState([]);
  const [totalLinguistPayHelperParts, setTotalLinguistPayHelperParts] =
    useState([]);

  const [linguistPay, setLinguistPay] = useState(
    isNewAssignment ? Number(prefilledData.LinguistPay || 0) : overlayData.LinguistPay,
  );

  const [additionalCost, setAdditionalCost] = useState(
    isNewAssignment ? Number(prefilledData.AdditionalCost || 0) : overlayData.AdditionalCost,
  );

  const [additionalLinguistCost, setAdditionalLinguistCost] = useState(
    isNewAssignment
      ? prefilledData.AdditionalLinguistCost || ""
      : overlayData.AdditionalLinguistCost,
  );

  const [rate, setRate] = useState(
    isNewAssignment ? Number(prefilledData.Rate || 0.2) : overlayData.Rate,
  );

  const [profit, setProfit] = useState(
    isNewAssignment ? prefilledData.Profit || "" : overlayData.Profit,
  );

  const [margin, setMargin] = useState(
    isNewAssignment ? prefilledData.Margin || "" : overlayData.Margin,
  );

  const [details, setDetails] = useState(
    isNewAssignment ? prefilledData.Details || "" : overlayData.Details,
  );

  // Email thread links (2026-10-07): translations support the same MULTIPLE
  // Outlook links as interpretations. The rows show the full links (hydrated
  // from the stored ids); only the id segments are persisted on save (a JSON
  // array behind the scenes). Each non-empty row must be a recognizable
  // Outlook link (or thread id) before saving.
  const [emailThreadLinks, setEmailThreadLinks] = useState(() => {
    const storedIds = isNewAssignment
      ? []
      : parseEmailThreadIds(overlayData.EmailThreadID);
    return storedIds.length ? storedIds.map(buildEmailThreadLink) : [""];
  });

  const originalEmailThreadIds = useMemo(
    () => (isNewAssignment ? [] : parseEmailThreadIds(overlayData.EmailThreadID)),
    [isNewAssignment, overlayData.EmailThreadID],
  );

  const emailThreadLinkStatuses = useMemo(
    () => emailThreadLinks.map(inspectEmailThreadInput),
    [emailThreadLinks],
  );

  // Ids to persist: every VALID row; invalid rows block the save.
  const emailThreadIds = useMemo(
    () =>
      emailThreadLinkStatuses
        .filter((status) => status.status === "valid")
        .map((status) => status.id),
    [emailThreadLinkStatuses],
  );

  const hasInvalidEmailThreadLink = emailThreadLinkStatuses.some(
    (status) => status.status === "invalid",
  );

  const handleEmailThreadLinkChange = useCallback((index, value) => {
    setEmailThreadLinks((previous) =>
      previous.map((link, linkIndex) => (linkIndex === index ? value : link)),
    );
  }, []);

  const handleAddEmailThreadLink = useCallback(() => {
    setEmailThreadLinks((previous) => [...previous, ""]);
  }, []);

  const handleRemoveEmailThreadLink = useCallback((index) => {
    setEmailThreadLinks((previous) =>
      previous.length <= 1
        ? [""]
        : previous.filter((_, linkIndex) => linkIndex !== index),
    );
  }, []);

  const handlePasteEmailThreadLink = useCallback(
    async (index) => {
      // Read first, decide after (2026-10-07): the row's value is NEVER
      // touched until a clipboard read has succeeded and returned usable
      // content. Clipboard access can fail outright (insecure context on
      // the LAN, permission denied, no clipboard API) — the row keeps
      // whatever it already held.
      let clipboardText;
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch (error) {
        console.error("Failed to read the clipboard:", error);
        showSnackbar(
          "Couldn't read the clipboard. Paste the link into the field manually.",
          theme,
          "error",
        );
        return;
      }

      const trimmed = String(clipboardText || "").trim();
      if (!trimmed) {
        showSnackbar(
          "Clipboard is empty. Copy the email thread link first.",
          theme,
          "info",
        );
        return;
      }

      const inspected = inspectEmailThreadInput(trimmed);
      if (inspected.status === "valid") {
        setEmailThreadLinks((previous) =>
          previous.map((link, linkIndex) =>
            linkIndex === index
              ? buildEmailThreadLink(inspected.id)
              : link,
          ),
        );
        showSnackbar("Email thread link pasted.", theme, "success");
        return;
      }

      // Unrecognized clipboard content must never replace a link that is
      // already in the row — one click would silently destroy it. It may
      // only fill a row that is still empty, so the user can see what was
      // on the clipboard and fix it.
      const rowHasValue = String(emailThreadLinks[index] || "").trim() !== "";
      if (rowHasValue) {
        showSnackbar(
          "That doesn't look like an Outlook email thread link — the existing link was kept.",
          theme,
          "warning",
        );
        return;
      }
      setEmailThreadLinks((previous) =>
        previous.map((link, linkIndex) =>
          linkIndex === index ? trimmed : link,
        ),
      );
      showSnackbar(
        "That doesn't look like an Outlook email thread link — please check it.",
        theme,
        "warning",
      );
    },
    [emailThreadLinks, showSnackbar, theme],
  );

  const [allowDetailsEditing, setAllowDetailsEditing] = useState(false);
  const [showLinguistPayAndOtherCosts, setShowLinguistPayAndOtherCosts] =
    useState(completing ? true : false);
  const [costRows, setCostRows] = useState([]);
  // Amounts for custom DB cost types (any type not in the built-in list),
  // keyed by typeKey so get/setCostValueByType work for ALL cost types.
  const [customCostValuesByType, setCustomCostValuesByType] = useState({});
  const [nextCostRowId, setNextCostRowId] = useState(1);

  const [nameError, setNameError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [projectNumberError, setProjectNumberError] = useState(false);
  const [projectNumberExists, setProjectNumberExists] = useState(false);
  const [translationTypeError, setTranslationTypeError] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [requestorError, setRequestorError] = useState(false);
  const [invoiceToError, setInvoiceToError] = useState(false);
  const [SourceLanguageError, setSourceLanguageError] = useState(false);
  const [linguistError, setLinguistError] = useState(false);
  const [translationStatusError, setTranslationStatusError] = useState(false);
  const [accountChargeError, setAccountChargeError] = useState(false);
  const [totalCostError, setTotalCostError] = useState(false);
  const [linguistPayError, setLinguistPayError] = useState(false);
  const [, setAdditionalCostError] = useState(false);
  const [, setAdditionalLinguistCostError] =
    useState(false);
  const [rateError, setRateError] = useState(false);
  const [, setDeadlineError] = useState(false);
  const [deliveryDateError, setDeliveryDateError] = useState(false);
  const [unitCountError, setUnitCountError] = useState(false);
  const [deliveryTypeError, setDeliveryTypeError] = useState(false);
  const [rateTypeError, setRateTypeError] = useState(false);
  const [paymentTypeError, setPaymentTypeError] = useState(false);
  const [, setNotarizationOrCOTCostError] =
    useState(false);
  const [, setPaymentFeesError] = useState(false);
  const [, setDeliveryCostError] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const [emailThreadLinkError, setEmailThreadLinkError] = useState(false);

  const [shouldValidate, setShouldValidate] = useState(false);

  const baseCostTypeOptions = useMemo(
    () => [
      {
        key: "linguistPay",
        label: "Linguist Pay",
        costTypeName: "Linguist Pay",
        sourceLegacyField: "LinguistPay",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: false,
        includeInLinguistPay: false,
      },
      {
        key: "additionalCost",
        label: "Additional Costs",
        costTypeName: "Additional Cost",
        sourceLegacyField: "AdditionalCost",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: true,
        includeInLinguistPay: false,
      },
      {
        key: "notarizationOrCOTCost",
        label: "Notarization/COT Cost",
        costTypeName: "Notarization Cost",
        sourceLegacyField: "NotarizationOrCOTCost",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: true,
        includeInLinguistPay: true,
      },
      {
        key: "additionalLinguistCost",
        label: "Additional Linguist Cost",
        costTypeName: "Additional Linguist Cost",
        sourceLegacyField: "AdditionalLinguistCost",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: true,
        includeInLinguistPay: true,
      },
      {
        key: "deliveryCost",
        label: "Delivery Cost",
        costTypeName: "Delivery Cost",
        sourceLegacyField: "DeliveryCost",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: true,
        includeInLinguistPay: true,
      },
      {
        key: "paymentFees",
        label: "Payment Fees",
        costTypeName: "Payment Fees",
        sourceLegacyField: "PaymentFees",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: false,
        includeInLinguistPay: true,
      },
    ],
    [],
  );
  const [costTypeRulesByName, setCostTypeRulesByName] = useState({});

  useEffect(() => {
    let isMounted = true;

    const loadCostTypeRules = async () => {
      try {
        const response = await api.get("/serviceCosts/types/by-category/Translation");
        const rows = Array.isArray(response.data) ? response.data : [];

        if (!isMounted) return;

        const byRuleKey = rows.reduce((acc, row) => {
          const normalizedName = String(row?.Name || "").trim().toLowerCase();
          const normalizedSource = String(row?.SourceLegacyField || "").trim().toLowerCase();
          const rule = {
            isEnabled: Boolean(row?.IsEnabledForCategory),
            includeInTotalCost: Boolean(row?.EffectiveIncludeInTotalCost),
            includeInAccountCharge: Boolean(row?.EffectiveIncludeInAccountCharge),
            includeInLinguistPay:
              row?.EffectiveIncludeInLinguistPay != null
                ? Boolean(row.EffectiveIncludeInLinguistPay)
                : null,
          };

          if (normalizedName) {
            acc[`name:${normalizedName}`] = rule;
          }
          if (normalizedSource) {
            acc[`source:${normalizedSource}`] = rule;
          }
          return acc;
        }, {});

        setCostTypeRulesByName(byRuleKey);
      } catch (error) {
        console.error("Error loading translation cost type rules:", error);
      }
    };

    loadCostTypeRules();

    return () => {
      isMounted = false;
    };
  }, []);

  const costTypeOptions = useMemo(
    () =>
      baseCostTypeOptions.map((option) => {
        const rule =
          costTypeRulesByName[
            `source:${String(option.sourceLegacyField || "").toLowerCase()}`
          ] ||
          costTypeRulesByName[
            `name:${String(option.costTypeName || "").toLowerCase()}`
          ] ||
          null;

        return {
          ...option,
          isEnabled: rule ? rule.isEnabled : true,
          effectiveIncludeInTotalCost: rule
            ? rule.includeInTotalCost
            : option.defaultIncludeInTotalCost,
          effectiveIncludeInAccountCharge: rule
            ? rule.includeInAccountCharge
            : option.defaultIncludeInAccountCharge,
          effectiveIncludeInLinguistPay:
            rule && rule.includeInLinguistPay != null
              ? rule.includeInLinguistPay
              : option.includeInLinguistPay,
        };
      }),
    [baseCostTypeOptions, costTypeRulesByName],
  );

  // Per-row inclusion flags: stored line flags win; fall back to the cost
  // type's effective defaults for pre-migration lines / new rows.
  // Payment Fees are ALWAYS excluded from Account Charge (Amine decision).
  const getRowInclusionFlags = useCallback(
    (typeKey, line) => {
      const option = costTypeOptions.find((opt) => opt.key === typeKey);
      const includeInTotalCost =
        line?.IncludeInTotalCost != null
          ? Boolean(line.IncludeInTotalCost)
          : Boolean(option?.effectiveIncludeInTotalCost ?? true);
      const includeInLinguistPay =
        line?.IncludeInLinguistPay != null
          ? Boolean(line.IncludeInLinguistPay)
          : Boolean(
              option?.effectiveIncludeInLinguistPay ??
                option?.includeInLinguistPay ??
                false,
            );
      const includeInAccountCharge =
        typeKey === "paymentFees"
          ? false
          : line?.IncludeInAccountCharge != null
            ? Boolean(line.IncludeInAccountCharge)
            : Boolean(option?.effectiveIncludeInAccountCharge ?? false);
      return { includeInTotalCost, includeInLinguistPay, includeInAccountCharge };
    },
    [costTypeOptions],
  );

  const sourceFieldToCostTypeKey = useMemo(
    () => ({
      LinguistPay: "linguistPay",
      AdditionalCost: "additionalCost",
      NotarizationOrCOTCost: "notarizationOrCOTCost",
      AdditionalLinguistCost: "additionalLinguistCost",
      DeliveryCost: "deliveryCost",
      PaymentFees: "paymentFees",
    }),
    [],
  );

  const costTypeNameToCostTypeKey = useMemo(
    () => ({
      "linguist pay": "linguistPay",
      "additional cost": "additionalCost",
      "additional costs": "additionalCost",
      "notarization cost": "notarizationOrCOTCost",
      "notarization/cot cost": "notarizationOrCOTCost",
      "notarization or cot cost": "notarizationOrCOTCost",
      "additional linguist cost": "additionalLinguistCost",
      "delivery cost": "deliveryCost",
      "payment fees": "paymentFees",
      "payment fee": "paymentFees",
    }),
    [],
  );

  const getCostTypeKeyFromCostLine = useCallback((line) => {
    if (!line) return "";

    const bySourceField = sourceFieldToCostTypeKey[line.SourceLegacyField];
    if (bySourceField) return bySourceField;

    const normalizedName = String(line.CostTypeName || "")
      .trim()
      .toLowerCase();

    return costTypeNameToCostTypeKey[normalizedName] || "";
  }, [sourceFieldToCostTypeKey, costTypeNameToCostTypeKey]);

  const getCostValueByType = useCallback(
    (typeKey) => {
      switch (typeKey) {
        case "linguistPay":
          return linguistPay;
        case "additionalLinguistCost":
          return additionalLinguistCost;
        case "additionalCost":
          return additionalCost;
        case "notarizationOrCOTCost":
          return notarizationOrCOTCost;
        case "deliveryCost":
          return deliveryCost;
        case "paymentFees":
          return paymentFees;
        default:
          return customCostValuesByType[typeKey] ?? "";
      }
    },
    [
      linguistPay,
      additionalLinguistCost,
      additionalCost,
      notarizationOrCOTCost,
      deliveryCost,
      paymentFees,
      customCostValuesByType,
    ],
  );

  const getOriginalCostValueByType = (typeKey) => {
    switch (typeKey) {
      case "linguistPay":
        return overlayData.LinguistPay;
      case "additionalLinguistCost":
        return overlayData.AdditionalLinguistCost;
      case "additionalCost":
        return overlayData.AdditionalCost;
      case "notarizationOrCOTCost":
        return overlayData.NotarizationOrCOTCost;
      case "deliveryCost":
        return overlayData.DeliveryCost;
      case "paymentFees":
        return overlayData.PaymentFees;
      default:
        return "";
    }
  };

  const getOriginalCostTypeByRowId = (rowId) => {
    const initialTypeKeys = [
      Number(overlayData.LinguistPay || 0) !== 0 ? "linguistPay" : null,
      Number(overlayData.AdditionalCost || 0) !== 0 ? "additionalCost" : null,
      Number(overlayData.NotarizationOrCOTCost || 0) !== 0 ? "notarizationOrCOTCost" : null,
      Number(overlayData.AdditionalLinguistCost || 0) !== 0 ? "additionalLinguistCost" : null,
      Number(overlayData.DeliveryCost || 0) !== 0 ? "deliveryCost" : null,
      Number(overlayData.PaymentFees || 0) !== 0 ? "paymentFees" : null,
    ].filter(Boolean);

    return initialTypeKeys[rowId - 1] || "";
  };

  const setCostValueByType = (typeKey, value) => {
    switch (typeKey) {
      case "linguistPay":
        setLinguistPay(value);
        setLinguistPayError(value === null);
        break;
      case "additionalLinguistCost":
        setAdditionalLinguistCost(value);
        setAdditionalLinguistCostError(value === null);
        break;
      case "additionalCost":
        setAdditionalCost(value);
        setAdditionalCostError(value === null);
        break;
      case "notarizationOrCOTCost":
        setNotarizationOrCOTCost(value);
        setNotarizationOrCOTCostError(value === null);
        break;
      case "deliveryCost":
        setDeliveryCost(value);
        setDeliveryCostError(value === null);
        break;
      case "paymentFees":
        setPaymentFees(value);
        setPaymentFeesError(value === null);
        break;
      default:
        setCustomCostValuesByType((prev) => ({
          ...prev,
          [typeKey]: value,
        }));
        break;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeCostRows = async () => {
      if (isNewAssignment) {
        if (!isMounted) return;

        const prefilledCostLines = Array.isArray(prefilledData?.CostLines)
          ? prefilledData.CostLines
          : [];

        const amountsFromCostLines = prefilledCostLines.reduce((acc, line) => {
          const typeKey = getCostTypeKeyFromCostLine(line);
          if (!typeKey) return acc;

          const lineAmount = Number(line?.Amount || 0);
          if (!Number.isFinite(lineAmount)) return acc;

          acc[typeKey] = Number(
            (Number(acc[typeKey] || 0) + lineAmount).toFixed(2),
          );
          return acc;
        }, {});

        const effectiveAmountByType = {
          linguistPay:
            amountsFromCostLines.linguistPay ?? Number(prefilledData.LinguistPay || 0),
          additionalCost:
            amountsFromCostLines.additionalCost ?? Number(prefilledData.AdditionalCost || 0),
          notarizationOrCOTCost:
            amountsFromCostLines.notarizationOrCOTCost ?? Number(prefilledData.NotarizationOrCOTCost || 0),
          additionalLinguistCost:
            amountsFromCostLines.additionalLinguistCost ?? Number(prefilledData.AdditionalLinguistCost || 0),
          deliveryCost:
            amountsFromCostLines.deliveryCost ?? Number(prefilledData.DeliveryCost || 0),
          paymentFees:
            amountsFromCostLines.paymentFees ?? Number(prefilledData.PaymentFees || 0),
        };

        const typeKeysFromEffectiveValues = Object.entries(effectiveAmountByType)
          .filter(([, amount]) => Number(amount || 0) !== 0)
          .map(([typeKey]) => typeKey);

        const orderedInitialTypeKeys = costTypeOptions
          .map((option) => option.key)
          .filter((key) => typeKeysFromEffectiveValues.includes(key));

        const lineByTypeKey = {};
        (Array.isArray(prefilledCostLines) ? prefilledCostLines : []).forEach(
          (line) => {
            const key = getCostTypeKeyFromCostLine(line);
            if (key && !lineByTypeKey[key]) lineByTypeKey[key] = line;
          },
        );

        const rows = orderedInitialTypeKeys.map((typeKey, index) => {
          const flags = getRowInclusionFlags(typeKey, lineByTypeKey[typeKey]);
          return {
            id: index + 1,
            typeKey,
            ...flags,
            originalIncludeInTotalCost: flags.includeInTotalCost,
            originalIncludeInLinguistPay: flags.includeInLinguistPay,
            originalIncludeInAccountCharge: flags.includeInAccountCharge,
          };
        });

        // 2026-10-06 (a) model: hydration markers for the manual-mode live
        // totals — prefilled cost lines count as "lines exist".
        hasCostLinesRef.current = prefilledCostLines.length > 0;
        costRowsHydratedRef.current = true;

        setLinguistPay(effectiveAmountByType.linguistPay);
        setAdditionalCost(effectiveAmountByType.additionalCost);
        setNotarizationOrCOTCost(effectiveAmountByType.notarizationOrCOTCost);
        setAdditionalLinguistCost(effectiveAmountByType.additionalLinguistCost);
        setDeliveryCost(effectiveAmountByType.deliveryCost);
        setPaymentFees(effectiveAmountByType.paymentFees);
        setCostRows(rows);
        setNextCostRowId(rows.length + 1);
        return;
      }

      let fetchedCostLines = [];
      if (overlayData?.id) {
        try {
          const response = await api.get(
            `/serviceCosts/service/${overlayData.id}/lines`,
          );
          fetchedCostLines = Array.isArray(response.data?.lines)
            ? response.data.lines
            : [];
        } catch (error) {
          console.error("Error loading service cost lines:", error);
        }
      }

      const mergedCostLines =
        fetchedCostLines.length > 0
          ? fetchedCostLines
          : Array.isArray(overlayData?.CostLines)
            ? overlayData.CostLines
            : [];

      const amountsFromCostLines = mergedCostLines.reduce((acc, line) => {
        const typeKey = getCostTypeKeyFromCostLine(line);
        if (!typeKey) return acc;

        const lineAmount = Number(line?.Amount || 0);
        if (!Number.isFinite(lineAmount)) return acc;

        acc[typeKey] = Number(
          (Number(acc[typeKey] || 0) + lineAmount).toFixed(2),
        );
        return acc;
      }, {});

      const effectiveAmountByType = {
        linguistPay:
          amountsFromCostLines.linguistPay ?? Number(overlayData.LinguistPay || 0),
        additionalCost:
          amountsFromCostLines.additionalCost ?? Number(overlayData.AdditionalCost || 0),
        notarizationOrCOTCost:
          amountsFromCostLines.notarizationOrCOTCost ??
          Number(overlayData.NotarizationOrCOTCost || 0),
        additionalLinguistCost:
          amountsFromCostLines.additionalLinguistCost ??
          Number(overlayData.AdditionalLinguistCost || 0),
        deliveryCost:
          amountsFromCostLines.deliveryCost ?? Number(overlayData.DeliveryCost || 0),
        paymentFees:
          amountsFromCostLines.paymentFees ?? Number(overlayData.PaymentFees || 0),
      };

      const totalLinguistPayFallback = Number(overlayData.TotalLinguistPay || 0);
      const hasAnyExplicitLinguistCostComponent =
        Number(effectiveAmountByType.linguistPay || 0) !== 0 ||
        Number(effectiveAmountByType.notarizationOrCOTCost || 0) !== 0 ||
        Number(effectiveAmountByType.additionalLinguistCost || 0) !== 0 ||
        Number(effectiveAmountByType.deliveryCost || 0) !== 0;

      if (
        !hasAnyExplicitLinguistCostComponent &&
        totalLinguistPayFallback !== 0
      ) {
        effectiveAmountByType.linguistPay = Number(
          totalLinguistPayFallback.toFixed(2),
        );
      }

      const typeKeysFromEffectiveValues = Object.entries(effectiveAmountByType)
        .filter(([, amount]) => Number(amount || 0) !== 0)
        .map(([typeKey]) => typeKey);

      const orderedInitialTypeKeys = costTypeOptions
        .map((option) => option.key)
        .filter((key) => typeKeysFromEffectiveValues.includes(key));

      const lineByTypeKey = {};
      (Array.isArray(mergedCostLines) ? mergedCostLines : []).forEach(
        (line) => {
          const key = getCostTypeKeyFromCostLine(line);
          if (key && !lineByTypeKey[key]) lineByTypeKey[key] = line;
        },
      );

      const rows = orderedInitialTypeKeys.map((typeKey, index) => {
        const flags = getRowInclusionFlags(typeKey, lineByTypeKey[typeKey]);
        return {
          id: index + 1,
          typeKey,
          ...flags,
          originalIncludeInTotalCost: flags.includeInTotalCost,
          originalIncludeInLinguistPay: flags.includeInLinguistPay,
          originalIncludeInAccountCharge: flags.includeInAccountCharge,
        };
      });

      if (!isMounted) return;

      // 2026-10-06 (a) model: hydration markers for the manual-mode live
      // totals — persisted cost lines win over the legacy fallback.
      hasCostLinesRef.current = mergedCostLines.length > 0;
      costRowsHydratedRef.current = true;

      setLinguistPay(effectiveAmountByType.linguistPay);
      setAdditionalCost(effectiveAmountByType.additionalCost);
      setNotarizationOrCOTCost(effectiveAmountByType.notarizationOrCOTCost);
      setAdditionalLinguistCost(effectiveAmountByType.additionalLinguistCost);
      setDeliveryCost(effectiveAmountByType.deliveryCost);
      setPaymentFees(effectiveAmountByType.paymentFees);
      setCostRows(rows);
      setNextCostRowId(rows.length + 1);
    };

    initializeCostRows();

    return () => {
      isMounted = false;
    };
  }, [
    isNewAssignment,
    prefilledData,
    overlayData,
    overlayData?.id,
    costTypeOptions,
    getCostTypeKeyFromCostLine,
    getRowInclusionFlags,
  ]);

  const addCostRow = () => {
    const usedKeys = new Set(costRows.map((row) => row.typeKey));
    const nextOption = costTypeOptions.find(
      (option) => option.isEnabled && !usedKeys.has(option.key),
    );
    if (!nextOption) return;

    setCostRows((prev) => [
      ...prev,
      {
        id: nextCostRowId,
        typeKey: nextOption.key,
        ...getRowInclusionFlags(nextOption.key),
      },
    ]);
    setNextCostRowId((prev) => prev + 1);
  };

  const removeCostRow = (rowId) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey) {
      setCostValueByType(row.typeKey, "");
    }
    setCostRows((prev) => prev.filter((item) => item.id !== rowId));
  };

  const updateCostRowType = (rowId, nextTypeKey) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey && row.typeKey !== nextTypeKey) {
      setCostValueByType(row.typeKey, "");
    }

    setCostRows((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? {
              ...item,
              typeKey: nextTypeKey,
              ...getRowInclusionFlags(nextTypeKey),
            }
          : item,
      ),
    );
  };

  const updateCostRowFlag = (rowId, flag, value) => {
    setCostRows((prev) =>
      prev.map((item) =>
        item.id === rowId ? { ...item, [flag]: value } : item,
      ),
    );
  };

  const [progress, setProgress] = useState("");
  const [progressMessage, setProgressMessage] = useState("");
  const [shakingFields, setShakingFields] = useState(new Set());

  const [, setHasChanges] = useState(false);
  const [
    disableAutomaticCalculationOfCharges,
    setDisableAutomaticCalculationOfCharges,
  ] = useState(
    isNewAssignment ? false : overlayData.DisableAutoChargeCalc || false,
  );

  const [createServicePerLanguage, setCreateServicePerLanguage] =
    useState(false);

  const initialDataRef = useRef(null);
  const addNewTranslationRef = useRef(null);
  const updateTranslationRef = useRef(null);
  const updateCurrentProgressRef = useRef(null);
  const saveTranslationRef = useRef(null);
  const attemptSaveRef = useRef(null);
  const backButtonLongPressTimerRef = useRef(null);
  const backButtonLongPressTriggeredRef = useRef(false);

  const randomInt = useCallback((min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }, []);

  const pickRandom = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return items[randomInt(0, items.length - 1)];
  }, [randomInt]);

  const pickRandomMany = useCallback((items, count) => {
    if (!Array.isArray(items) || items.length === 0 || count <= 0) return [];
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, shuffled.length));
  }, []);

  const clearBackButtonLongPress = useCallback(() => {
    if (backButtonLongPressTimerRef.current) {
      clearTimeout(backButtonLongPressTimerRef.current);
      backButtonLongPressTimerRef.current = null;
    }
  }, []);

  const getRateForTranslationType = useCallback((typeName) => {
    const normalizedType = String(typeName || "").trim().toLowerCase();
    if (normalizedType.includes("proofread")) {
      return 0.08;
    }
    return 0.2;
  }, []);

  const getDateCount = useCallback(async (dateValue) => {
    if (!dateValue) {
      return 0;
    }

    const dateStr = dayjs(dateValue).format("YYYY-MM-DD");

    try {
      const response = await api.get("/services/interpretations/date-count", {
        params: { date: dateStr },
      });

      const count = parseInt(response.data.count, 10);
      return Number.isFinite(count) ? count : 0;
    } catch (error) {
      console.error("[Translation:getDateCount] Error fetching date count:", error);
      return 0;
    }
  }, []);

  const generateProjectNumber = useCallback((dateValue, count) => {
    if (!dateValue) return "";

    const baseDate = dayjs(dateValue);
    const index = count + 1;
    const paddedIndex = index < 10 ? `0${index}` : `${index}`;
    return baseDate.format("YYMMDD") + paddedIndex;
  }, []);

  const fillDetails = useCallback((
    requestor,
    name,
    unitCount,
    sourceLanguage,
    targetLanguages,
    translationType,
  ) => {
    const req = requestor ? requestor.trim() : "";
    const nm = name ? name.trim() : "";
    const lang = sourceLanguage ? sourceLanguage.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasUnitCount = typeof unitCount === "number" && unitCount > 0;
    const hasSourceLanguage = lang.length > 0;
    const hasSelectedLanguage =
      Array.isArray(targetLanguages) && targetLanguages.length > 0;

    const isMinimum =
      unitTypeName === "words" &&
      typeof unitCount === "number" &&
      unitCount > 0 &&
      unitCount < 245;

    const detailsParts = [];

    if (hasRequestor) {
      detailsParts.push(req);
    }

    if (hasName) {
      detailsParts.push(nm);
    }

    if (hasUnitCount) {
      const unitLabel =
        Number(unitCount) === 1 ? unitTypeName : `${unitTypeName}s`;

      detailsParts.push(`${Number(unitCount).toLocaleString()} ${unitLabel}`);
    }

    if (hasSourceLanguage && hasSelectedLanguage) {
      let languagePart = lang + " to ";

      const selectedLangNames = targetLanguages.reduce((acc, item, index) => {
        if (index === targetLanguages.length - 2) {
          return acc + item.Name + ", and ";
        } else if (index < targetLanguages.length - 1) {
          return acc + item.Name + ", ";
        } else {
          return acc + item.Name;
        }
      }, "");

      languagePart += selectedLangNames + " " + translationType;

      detailsParts.push(languagePart);
    }

    if (isMinimum) {
      detailsParts.push("Minimum");
    }

    if (detailsParts.length > 0) {
      setDetails(detailsParts.join(" - "));
    } else {
      setDetails("");
    }
  }, [unitTypeName]);

  const randomizeFormForAmine = useCallback(async () => {
    if (!isAmineUser || !isNewAssignment || !isEditable) return;

    const randomDate = dayjs().add(randomInt(-30, 120), "day").startOf("day");
    const randomDeadline = randomDate.add(randomInt(1, 28), "day");
    const randomName = `${pickRandom(LOREM_NAME_SEEDS) || "Lorem ipsum"} ${randomInt(100, 999)}`;

    const randomTranslationType =
      pickRandom(translationSubtypeOptions.map((option) => option?.Name).filter(Boolean)) ||
      pickRandom(overlayData.TranslationTypes || []) ||
      translationType;

    const randomClient = pickRandom(overlayData.Clients || []);
    const randomRequestor = pickRandom(randomClient?.Requestors || []);

    const allLanguages = overlayData.Languages || [];
    const randomSourceLanguage = pickRandom(allLanguages) || sourceLanguage;
    const sourceLanguageKey = String(randomSourceLanguage?.Name || "").trim().toLowerCase();
    const isSourceEnglish = sourceLanguageKey === "english";

    const targetLanguagePool = allLanguages.filter((language) => {
      if (!language || !randomSourceLanguage) return false;
      if (language.id != null && randomSourceLanguage.id != null) {
        return language.id !== randomSourceLanguage.id;
      }
      return (language.Name || "").trim().toLowerCase() !== sourceLanguageKey;
    });

    const maxTargetLanguages = Math.min(
      isSourceEnglish ? 6 : 5,
      targetLanguagePool.length,
    );
    const randomTargetCount =
      maxTargetLanguages > 0 ? randomInt(1, maxTargetLanguages) : 0;
    const chosenTargets = pickRandomMany(targetLanguagePool, randomTargetCount);
    const normalizedTargets = normalizeTargetLanguages(
      chosenTargets,
      randomSourceLanguage,
    ).slice(0, 6);

    const selectedLanguageNames = [
      randomSourceLanguage?.Name,
      ...normalizedTargets.map((language) => language?.Name),
    ]
      .map((languageName) => String(languageName || "").trim())
      .filter(Boolean);

    const languageMatchedLinguists = (overlayData.Linguists || []).filter(
      (linguistOption) => {
        if (String(linguistOption?.Name || "").trim().toUpperCase() === "N/A") {
          return false;
        }

        const linguistLanguages = linguistOption?.LinguistLanguages || [];
        return linguistLanguages.some((linguistLanguage) =>
          selectedLanguageNames.includes(
            String(linguistLanguage?.Language?.Name || "").trim(),
          ),
        );
      },
    );

    const randomLinguist = pickRandom(languageMatchedLinguists);

    const selectedRate = getRateForTranslationType(randomTranslationType);

    const randomWordCount = randomInt(501, 5999);
    const wordsUnitType =
      unitTypes.find((unit) => {
        const normalizedName = String(unit?.Name || "").trim().toLowerCase();
        return normalizedName === "word" || normalizedName === "words";
      }) || null;

    setDate(randomDate);
    setDeadline(randomDeadline);
    const randomDateCount = await getDateCount(randomDate);
    setProjectNumber(generateProjectNumber(randomDate, randomDateCount));
    setProjectNumberExists(false);
    setName(randomName);
    if (randomTranslationType) setTranslationType(randomTranslationType);
    if (randomClient) setClient(randomClient);
    setRequestor(randomRequestor || "");
    if (randomSourceLanguage) setSourceLanguage(randomSourceLanguage);
    setTargetLanguages(normalizedTargets);
    setNumberOfLanguages(normalizedTargets.length);
    setRate(selectedRate);
    if (randomLinguist) {
      setLinguist(randomLinguist);
    } else {
      setLinguist("");
    }
    setUnitCount(randomWordCount);
    if (wordsUnitType?.id) setUnitTypeId(wordsUnitType.id);

    const effectiveNumberOfLanguages =
      createServicePerLanguage && normalizedTargets.length > 1
        ? 1
        : normalizedTargets.length;

    let randomAccountCharge =
      randomWordCount * effectiveNumberOfLanguages * selectedRate +
      parseFloat(notarizationOrCOTCost || 0) +
      parseFloat(additionalLinguistCost || 0) +
      parseFloat(additionalCost || 0) +
      parseFloat(deliveryCost || 0) +
      parseFloat(paymentFees || 0);
    randomAccountCharge = randomAccountCharge >= 49 ? randomAccountCharge : 49;
    setAccountCharge(Number(randomAccountCharge).toFixed(2));

    fillDetails(
      randomRequestor?.Name,
      randomName,
      randomWordCount,
      randomSourceLanguage?.Name,
      normalizedTargets,
      randomTranslationType,
    );
  }, [
    fillDetails,
    isAmineUser,
    isEditable,
    isNewAssignment,
    getRateForTranslationType,
    normalizeTargetLanguages,
    overlayData.Clients,
    overlayData.Languages,
    overlayData.Linguists,
    overlayData.TranslationTypes,
    getDateCount,
    generateProjectNumber,
    pickRandom,
    pickRandomMany,
    randomInt,
    createServicePerLanguage,
    notarizationOrCOTCost,
    additionalLinguistCost,
    additionalCost,
    deliveryCost,
    paymentFees,
    sourceLanguage,
    translationSubtypeOptions,
    translationType,
    unitTypes,
  ]);

  const handleBackButtonPressStart = useCallback(() => {
    if (!isAmineUser || !isNewAssignment) return;
    clearBackButtonLongPress();
    backButtonLongPressTriggeredRef.current = false;
    backButtonLongPressTimerRef.current = setTimeout(() => {
      backButtonLongPressTriggeredRef.current = true;
      void randomizeFormForAmine();
    }, LONG_PRESS_AUTOFILL_MS);
  }, [
    clearBackButtonLongPress,
    isAmineUser,
    isNewAssignment,
    randomizeFormForAmine,
  ]);

  const handleBackButtonPressEnd = useCallback(() => {
    clearBackButtonLongPress();
  }, [clearBackButtonLongPress]);

  const handleBackButtonClick = useCallback(() => {
    if (backButtonLongPressTriggeredRef.current) {
      backButtonLongPressTriggeredRef.current = false;
      return;
    }

    releaseLock();
    setOverlayName("");
  }, [releaseLock, setOverlayName]);

  useEffect(() => {
    return () => {
      clearBackButtonLongPress();
    };
  }, [clearBackButtonLongPress]);

  /**
   * Helper function to get shake animation styles
   */
  const getShakeSx = (fieldId) => {
    const isShaking = shakingFields.has(fieldId);
    return isShaking
      ? {
          animation: "shake 0.6s",
          "@keyframes shake": {
            "0%, 100%": { transform: "translateX(0)" },
            "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
            "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
          },
        }
      : {};
  };

  /**
   * Trigger shake animation on missing required fields
   */
  const triggerMissingFieldsShake = () => {
    const requiredFields = [
      { fieldId: "Date", value: date },
      { fieldId: "Deadline", value: deadline },
      { fieldId: "ProjectNumber", value: projectNumber },
      { fieldId: "TranslationType", value: translationType },
      { fieldId: "SourceLanguage", value: sourceLanguage },
      { fieldId: "Client", value: client },
      { fieldId: "Requestor", value: requestor },
      { fieldId: "Name", value: name },
      { fieldId: "UnitType", value: unitTypeId },
      { fieldId: "UnitCount", value: unitCount },
      // Visible rate is Unit Rate (legacy Rate field removed 2026-10-01):
      // validate the effective rate — Unit Rate first, legacy Rate fallback —
      // and shake the Unit Rate field when both are missing.
      {
        fieldId: "UnitRate",
        value: unitRate !== null && unitRate !== "" ? unitRate : rate,
      },
      { fieldId: "Linguist", value: linguist },
      { fieldId: "TargetLanguages", value: targetLanguages },
      { fieldId: "TranslationStatus", value: translationStatus },
      { fieldId: "AccountCharge", value: accountCharge },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) =>
        field.value === null ||
        field.value === "" ||
        (Array.isArray(field.value) && field.value.length === 0),
    );

    if (missingRequiredFields.length > 0) {
      const newShakingFields = new Set(
        missingRequiredFields.map((field) => field.fieldId),
      );
      setShakingFields(newShakingFields);

      setTimeout(() => {
        setShakingFields(new Set());
      }, 600);

      return false;
    }

    return true;
  };

  const updateCurrentProgress = () => {
    let progress = 0;
    const total = 18;

    if (date !== null) progress++;
    if (deadline !== null) progress++;
    if (projectNumber !== null && projectNumber !== "") progress++;
    if (translationType !== null && translationType !== "") progress++;
    if (sourceLanguage !== null && sourceLanguage !== "") progress++;
    if (client !== null && client !== "") progress++;
    if (requestor !== null && requestor !== "") progress++;
    if (name !== null && name !== "") progress++;
    if (unitTypeId !== null && unitTypeId !== "") progress++;
    if (unitCount !== null && unitCount !== "") progress++;
    if (
      (unitRate !== null && unitRate !== "") ||
      (rate !== null && rate !== "")
    )
      progress++;
    if (linguist !== null && linguist !== "") progress++;
    if (targetLanguages !== null && targetLanguages.length > 0) progress++;
    if (translationStatus !== null && translationStatus !== "") progress++;
    if (accountCharge !== null && accountCharge !== "") progress++;
    if (details !== null && details !== "") progress++;
    if ((totalCost !== null && totalCost !== "") || !completing) progress++;
    if ((linguistPay !== null && linguistPay !== "") || !completing) progress++;

    const percentProgress = (progress * 100) / total;
    const message =
      progress === total
        ? "All Done!"
        : `${progress}/${total} fields completed`;
    setProgressMessage(Number(percentProgress).toFixed(2) + "%: " + message);
    setProgress(percentProgress);
  };

  const applyTargetLanguages = (
    nextTargetLanguages,
    selectedSourceLanguage = sourceLanguage,
  ) => {
    const normalizedTargetLanguages = normalizeTargetLanguages(
      nextTargetLanguages,
      selectedSourceLanguage,
    );

    fillDetails(
      requestor.Name,
      name,
      Number(unitCount),
      selectedSourceLanguage?.Name || "",
      normalizedTargetLanguages,
      translationType,
    );

    setNumberOfLanguages(normalizedTargetLanguages.length);
    setTargetLanguages(normalizedTargetLanguages);
  };

  // Helper function to generate details string for a single language (for per-language services)
  const generateDetailsForLanguage = (
    requestorName,
    serviceName,
    unitCount,
    sourceLanguageName,
    targetLanguageName,
    translationType,
  ) => {
    const req = requestorName ? requestorName.trim() : "";
    const nm = serviceName ? serviceName.trim() : "";
    const lang = sourceLanguageName ? sourceLanguageName.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasUnitCount = typeof unitCount === "number" && unitCount > 0;
    const hasSourceLanguage = lang.length > 0;
    const hasTargetLanguage =
      targetLanguageName && targetLanguageName.trim().length > 0;

    const isMinimum =
      unitTypeName === "words" &&
      typeof unitCount === "number" &&
      unitCount > 0 &&
      unitCount < 245;

    const detailsParts = [];

    if (hasRequestor) {
      detailsParts.push(req);
    }

    if (hasName) {
      detailsParts.push(nm);
    }

    if (hasUnitCount) {
      const unitLabel =
        Number(unitCount) === 1 ? unitTypeName : `${unitTypeName}s`;

      detailsParts.push(`${Number(unitCount).toLocaleString()} ${unitLabel}`);
    }

    if (hasSourceLanguage && hasTargetLanguage) {
      const languagePart = `${lang} to ${targetLanguageName} ${translationType}`;
      detailsParts.push(languagePart);
    }

    if (isMinimum) {
      detailsParts.push("Minimum");
    }

    return detailsParts.length > 0 ? detailsParts.join(" - ") : "";
  };

  dayjs.extend(utc);
  dayjs.extend(durationPlugin);

  // 2026-10-06 (a) model: live totals for the manual-mode refill paths — the
  // same sums the auto-calc effect applies (base Linguist Pay + every cost
  // line flagged includeInLinguistPay / includeInTotalCost). Used when the
  // user CLEARS an override so the field immediately shows the live calc.
  const computeLiveManualTotals = useCallback(() => {
    const rowFlagByType = costRows.reduce((acc, row) => {
      acc[row.typeKey] = {
        includeInTotalCost: row.includeInTotalCost,
        includeInLinguistPay: row.includeInLinguistPay,
      };
      return acc;
    }, {});
    const getRowFlag = (typeKey, flag, fallback) => {
      const rowFlags = rowFlagByType[typeKey];
      return rowFlags && typeof rowFlags[flag] === "boolean"
        ? rowFlags[flag]
        : fallback;
    };
    const sumExtras = (flagKey, fallbackKey) =>
      costTypeOptions
        .filter(
          (opt) =>
            opt.key !== "linguistPay" &&
            getRowFlag(opt.key, flagKey, Boolean(opt[fallbackKey])),
        )
        .reduce(
          (sum, opt) => sum + parseFloat(getCostValueByType(opt.key) || 0),
          0,
        );
    const base = parseFloat(linguistPay || 0);
    return {
      totalCost: (
        base + sumExtras("includeInTotalCost", "effectiveIncludeInTotalCost")
      ).toFixed(2),
      totalLinguistPay: (
        base + sumExtras("includeInLinguistPay", "effectiveIncludeInLinguistPay")
      ).toFixed(2),
    };
  }, [costRows, costTypeOptions, getCostValueByType, linguistPay]);

  // 2026-10-06 (a) model: manual-mode live value for Total Linguist Pay —
  // legacy services with NO cost-line data at all keep the stored-TotalCost
  // fallback; otherwise the live lines-sum wins.
  const getManualTlPayLiveValue = useCallback(() => {
    if (!hasCostLinesRef.current && costRows.length === 0) {
      return legacyTotalCostTlPayFallbackRef.current == null
        ? ""
        : Number(legacyTotalCostTlPayFallbackRef.current).toFixed(2);
    }
    return computeLiveManualTotals().totalLinguistPay;
  }, [costRows, computeLiveManualTotals]);

  // Auto-calculate charges based on unit count, rate, and other factors
  useEffect(() => {
    // 2026-10-06 (a) model: manual mode (DisableAutoChargeCalc) is no longer
    // skipped up-front — the totals compute live and the write site at the
    // bottom of this effect applies the per-field override guards.

    const parsedUnitCount = Number(unitCount);
    const parsedRate = Number(rate);
    const parsedUnitRate = Number(unitRate);
    const parsedLanguageCount = Number(numberOfLanguages);

    const hasValidUnitCount =
      unitCount !== null && unitCount !== "" && Number.isFinite(parsedUnitCount);
    const hasValidRate =
      rate !== null && rate !== "" && Number.isFinite(parsedRate);
    const hasValidUnitRate =
      unitRate !== null && unitRate !== "" && Number.isFinite(parsedUnitRate);
    const hasValidLanguageCount =
      numberOfLanguages !== null && Number.isFinite(parsedLanguageCount);

    // UnitRate takes precedence; fall back to Rate for backward compat
    const effectiveRate = hasValidUnitRate ? parsedUnitRate : parsedRate;

    // If required fields are missing/invalid, don't calculate
    if (
      !hasValidUnitCount ||
      (!hasValidRate && !hasValidUnitRate) ||
      !hasValidLanguageCount
    ) {
      return;
    }

    // Use 1 for calculations when creating service per language
    const effectiveNumberOfLanguages =
      createServicePerLanguage && targetLanguages.length > 1
        ? 1
        : numberOfLanguages;

    // Part builders for the formula helper (2026-10-01): a `tip` turns that
    // number into a hover tooltip; money renders 2dp, counts stay raw.
    const moneyPart = (value, tip) => ({
      text: Number(value || 0).toFixed(2),
      tip,
    });
    const numPart = (value, tip) => ({ text: String(value), tip });
    const textPart = (text) => ({ text, tip: null });

    // Per-row inclusion flags drive the totals (flag-driven, matches Settings).
    const rowFlagByType = costRows.reduce((acc, row) => {
      acc[row.typeKey] = {
        includeInTotalCost: row.includeInTotalCost,
        includeInLinguistPay: row.includeInLinguistPay,
        includeInAccountCharge: row.includeInAccountCharge,
      };
      return acc;
    }, {});

    const getRowFlag = (typeKey, flag, fallback) => {
      const rowFlags = rowFlagByType[typeKey];
      return rowFlags && typeof rowFlags[flag] === "boolean"
        ? rowFlags[flag]
        : fallback;
    };

    const chargeExtras = costTypeOptions.filter(
      (opt) =>
        opt.key !== "linguistPay" &&
        getRowFlag(
          opt.key,
          "includeInAccountCharge",
          Boolean(opt.effectiveIncludeInAccountCharge),
        ),
    );
    const extraChargeSum = chargeExtras.reduce(
      (sum, opt) => sum + parseFloat(getCostValueByType(opt.key) || 0),
      0,
    );
    const chargeExtraParts = chargeExtras.flatMap((opt) => [
      textPart(" + "),
      {
        text: parseFloat(getCostValueByType(opt.key) || 0).toFixed(2),
        tip: opt.label,
      },
    ]);

    const lingPayExtras = costTypeOptions.filter(
      (opt) =>
        opt.key !== "linguistPay" &&
        getRowFlag(
          opt.key,
          "includeInLinguistPay",
          Boolean(opt.effectiveIncludeInLinguistPay),
        ),
    );
    const extraLingPaySum = lingPayExtras.reduce(
      (sum, opt) => sum + parseFloat(getCostValueByType(opt.key) || 0),
      0,
    );
    const lingPayExtraParts = lingPayExtras.flatMap((opt) => [
      textPart(" + "),
      {
        text: parseFloat(getCostValueByType(opt.key) || 0).toFixed(2),
        tip: opt.label,
      },
    ]);

    const totCostExtras = costTypeOptions.filter(
      (opt) =>
        opt.key !== "linguistPay" &&
        getRowFlag(
          opt.key,
          "includeInTotalCost",
          Boolean(opt.effectiveIncludeInTotalCost),
        ),
    );
    const extraTotCostSum = totCostExtras.reduce(
      (sum, opt) => sum + parseFloat(getCostValueByType(opt.key) || 0),
      0,
    );
    const totCostExtraParts = totCostExtras.flatMap((opt) => [
      textPart(" + "),
      {
        text: parseFloat(getCostValueByType(opt.key) || 0).toFixed(2),
        tip: opt.label,
      },
    ]);

    let charge =
      parsedUnitCount *
        parseFloat(effectiveNumberOfLanguages) *
        effectiveRate +
      extraChargeSum;

    const chargeParts = [
      textPart("( "),
      numPart(parsedUnitCount, "Unit count"),
      textPart(" x "),
      numPart(effectiveNumberOfLanguages, "Number of languages"),
      textPart(" x "),
      moneyPart(effectiveRate, "Rate per unit ($)"),
      textPart(" )"),
      ...chargeExtraParts,
    ];

    const totalLinguistPayValue =
      parseFloat(linguistPay || 0) + extraLingPaySum;

    const totCost = parseFloat(linguistPay || 0) + extraTotCostSum;

    // Total Linguist Pay / Total Cost (2026-10-01): base + included extras,
    // every number hover-explained — the base is tagged so it can't be
    // confused with a named cost type.
    const totalLingPayParts = [
      {
        text: parseFloat(linguistPay || 0).toFixed(2),
        tip: "Linguist Pay (base)",
      },
      ...lingPayExtraParts,
    ];

    const totCostParts = [
      {
        text: parseFloat(linguistPay || 0).toFixed(2),
        tip: "Linguist Pay (base)",
      },
      ...totCostExtraParts,
    ];

    // 2026-10-06 (a) model — manual mode (DisableAutoChargeCalc): totals are
    // computed exactly like the auto path, but each write is override-guarded
    // (typed values win). ONLY the non-overridden totals are written here; the
    // Account Charge / Profit / Margin / helper-part writes below keep today's
    // manual behavior. AUTO mode stays unchanged.
    if (disableAutomaticCalculationOfCharges) {
      if (costRowsHydratedRef.current) {
        if (!totalLinguistPayOverriddenRef.current) {
          setTotalLinguistPay(getManualTlPayLiveValue());
        }
        if (!totalCostOverriddenRef.current) {
          setTotalCost(Number(totCost).toFixed(2));
        }
      }
      return;
    }

    // Apply $49 minimum for automatic calculations
    charge = charge >= 49 ? charge : 49;
    setAccountCharge(Number(charge).toFixed(2));
    setAccountChargeHelperParts(chargeParts);

    setTotalCostHelperParts(totCostParts);
    setTotalLinguistPayHelperParts(totalLingPayParts);
    setTotalLinguistPay(Number(totalLinguistPayValue).toFixed(2));
    setTotalCost(Number(totCost).toFixed(2));

    const prof = parseFloat(charge) - parseFloat(totCost);
    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const marginValue = (prof / charge) * 100;
      setMargin(
        isNaN(marginValue) || !isFinite(marginValue)
          ? 0
          : marginValue.toFixed(2),
      );
    }
  }, [
    unitCount,
    numberOfLanguages,
    rate,
    unitRate,
    notarizationOrCOTCost,
    additionalLinguistCost,
    additionalCost,
    deliveryCost,
    paymentFees,
    linguistPay,
    costRows,
    costTypeOptions,
    customCostValuesByType,
    disableAutomaticCalculationOfCharges,
    createServicePerLanguage,
    targetLanguages,
    getCostValueByType,
    getManualTlPayLiveValue,
  ]);

  // Recalculate profit and margin whenever accountCharge or totalCost changes
  useEffect(() => {
    const charge = parseFloat(accountCharge || 0);
    const totCost = parseFloat(totalCost || 0);
    const prof = parseFloat(charge) - parseFloat(totCost);

    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const marginValue = (prof / charge) * 100;
      setMargin(
        isNaN(marginValue) || !isFinite(marginValue)
          ? 0
          : marginValue.toFixed(2),
      );
    }
  }, [accountCharge, totalCost]);

  const validateFields = () => {
    setNameError(name === null);
    setDateError(date === null);
    setProjectNumberError(projectNumber === null);
    setTranslationTypeError(translationType === null);
    setClientError(client === null);
    setRequestorError(requestor === null);
    setSourceLanguageError(sourceLanguage === null);
    setLinguistError(linguist === null);
    // Effective-rate gate (2026-10-01): the visible field is Unit Rate; the
    // legacy Rate stays the fallback — block only when BOTH are missing.
    setRateError(
      (unitRate === null || unitRate === "") && (rate === null || rate === ""),
    );
    setTranslationStatusError(translationStatus === null);
    setAccountChargeError(accountCharge === null);
    setTotalCostError(totalCost === null);
    setLinguistPayError(linguistPay === null);
    setDetailsError(details === null);
    setEmailThreadLinkError(hasInvalidEmailThreadLink);
  };

  // Check if assignment was paid and if critical fields have changed
  const checkPaidWarning = () => {
    // Only check for existing assignments
    if (isNewAssignment) return false;

    // Check if the assignment was paid
    const wasPaid = overlayData.Paid === true || overlayData.Paid === 1;

    if (!wasPaid) return false;

    // Check if critical fields have changed
    const criticalChanges = [];

    // Check if client changed
    if (client?.id !== overlayData.Client?.id) {
      criticalChanges.push("Client");
    }

    // Check if linguist changed
    if (linguist?.id !== overlayData.Linguist?.id) {
      criticalChanges.push("Linguist");
    }

    // Check if account charge changed
    if (Number(accountCharge) !== Number(overlayData.AccountCharge)) {
      criticalChanges.push("Account Charge");
    }

    // Check if total cost changed
    if (Number(totalCost) !== Number(overlayData.TotalCost)) {
      criticalChanges.push("Total Cost");
    }

    // Check if linguist pay changed
    if (Number(linguistPay) !== Number(overlayData.LinguistPay)) {
      criticalChanges.push("Linguist Pay");
    }

    if (criticalChanges.length > 0) {
      let message = "⚠️ WARNING: This assignment has been marked as PAID.\n\n";
      message += `You are changing: ${criticalChanges.join(", ")}\n\n`;

      // Special check for linguist change - check if the linguist was paid
      if (criticalChanges.includes("Linguist")) {
        message +=
          "🔹 You are changing the linguist for this paid assignment.\n";
        message += `   Previous linguist: ${overlayData.Linguist?.Name || "None"}\n`;
        message += `   New linguist: ${linguist?.Name || "None"}\n\n`;
      }

      message += "Are you sure you want to proceed with these changes?";

      setPaidWarningMessage(message);
      return true;
    }

    return false;
  };

  const buildDynamicCostLines = () => {
    const toAmount = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
    };

    const lineConfigByType = costTypeOptions.reduce((acc, option) => {
      acc[option.key] = {
        CostTypeName: option.costTypeName,
        SourceLegacyField: option.sourceLegacyField,
        IncludeInTotalCost: option.effectiveIncludeInTotalCost,
        IncludeInAccountCharge:
          option.key === "paymentFees"
            ? false
            : option.effectiveIncludeInAccountCharge,
        IncludeInLinguistPay: option.effectiveIncludeInLinguistPay ?? false,
      };
      return acc;
    }, {});

    // Per-row flags win over the type-level defaults.
    const rowFlagByType = costRows.reduce((acc, row) => {
      acc[row.typeKey] = {
        IncludeInTotalCost: row.includeInTotalCost,
        IncludeInLinguistPay: row.includeInLinguistPay,
        IncludeInAccountCharge: row.includeInAccountCharge,
      };
      return acc;
    }, {});

    const applyRowFlags = (config, typeKey) => {
      const rowFlags = rowFlagByType[typeKey];
      if (!rowFlags) return config;
      return {
        ...config,
        IncludeInTotalCost: rowFlags.IncludeInTotalCost,
        IncludeInLinguistPay: rowFlags.IncludeInLinguistPay,
        IncludeInAccountCharge:
          typeKey === "paymentFees" ? false : rowFlags.IncludeInAccountCharge,
      };
    };

    // Build lines from costRows (user-added rows)
    const costRowLines = costRows
      .map((row, index) => {
        const config = lineConfigByType[row.typeKey];
        if (!config) return null;

        return {
          ...applyRowFlags(config, row.typeKey),
          Amount: toAmount(getCostValueByType(row.typeKey)),
          DisplayOrder: (index + 1) * 10,
        };
      })
      .filter(Boolean)
      .filter((line) => line.Amount > 0);

    // Also include base cost types (linguistPay, paymentFees, etc) that have non-zero values
    // even if they're not explicitly in costRows (handles auto-calculated values)
    const baseTypeKeys = new Set(["linguistPay", "paymentFees"]);
    const costRowTypeKeys = new Set(costRows.map((row) => row.typeKey));

    const baseLines = Array.from(baseTypeKeys)
      .filter((typeKey) => !costRowTypeKeys.has(typeKey) && lineConfigByType[typeKey])
      .map((typeKey, index) => {
        const config = lineConfigByType[typeKey];
        const amount = toAmount(getCostValueByType(typeKey));
        if (amount === 0) return null;

        return {
          ...applyRowFlags(config, typeKey),
          Amount: amount,
          DisplayOrder: (costRows.length + index + 1) * 10,
        };
      })
      .filter(Boolean);

    return [...costRowLines, ...baseLines];
  };

  function addNewTranslation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const dynamicCostLines = buildDynamicCostLines();
    const assignmentCreationSource =
      overlayData?.CreationSource === "quick_create"
        ? "quick_create"
        : "screen_button";

    const normalizedUnitTypeId = unitTypeId === "" ? null : Number(unitTypeId);
    const normalizedUnitCount = unitCount === "" ? null : Number(unitCount);
    const legacyWordCount =
      unitTypeName === "words" ? normalizedUnitCount : null;

    // Check if we should create one service per language
    if (createServicePerLanguage && targetLanguages.length > 1) {
      // Create one service for each target language
      const servicePromises = targetLanguages.map((targetLanguage, index) => {
        const currentProjectNumber = String(Number(projectNumber) + index);

        // Generate details specific to this language
        const languageSpecificDetails = generateDetailsForLanguage(
          requestor?.Name || "",
          name,
          normalizedUnitCount,
          sourceLanguage?.Name || "",
          targetLanguage.Name,
          translationType,
        );

        const data = {
          Assignment: {
            id: 0,
            Date: date.format("YYYY-MM-DD"),
            Deadline:
              deadline && deadline.isValid()
                ? deadline.format("YYYY-MM-DD")
                : null,
            DeliveryDate:
              deliveryDate && deliveryDate.isValid()
                ? deliveryDate.format("YYYY-MM-DD")
                : null,
            ProjectNumber: currentProjectNumber,
            CheckingDate: null,
            ServiceCategory: "Translation",
            Name: name,
            Details: languageSpecificDetails,
            TranslationStatus:
              translationStatus === "" ? null : translationStatus,
            AccountCharge: accountCharge === "" ? null : accountCharge,
            // 2026-10-08 (stored-columns-only — supersedes the 2026-10-06 (a)
            // model): persist the DISPLAYED totals so Portal readers can read
            // the stored columns; empty field → null (unchanged).
            TotalCost: totalCost === "" ? null : totalCost,
            TotalLinguistPay:
              totalLinguistPay === ""
                ? null
                : Number(totalLinguistPay).toFixed(2),
            AdditionalLinguistCost:
              additionalLinguistCost === "" ? null : additionalLinguistCost,
            AdditionalCost: additionalCost === "" ? null : additionalCost,
            LinguistPay: linguistPay === "" ? null : linguistPay,
            NotarizationOrCOTCost:
              notarizationOrCOTCost === "" ? null : notarizationOrCOTCost,
            DeliveryCost: deliveryCost === "" ? null : deliveryCost,
            DeliveryType: deliveryType === "" ? null : deliveryType,
            PaymentFees: paymentFees === "" ? null : paymentFees,
            PaymentType: paymentType === "" ? null : paymentType,
            Profit: profit === "" ? null : profit,
            Margin: margin === "" ? null : margin,
            TranslationType: translationType,
            ServiceSubtypeID: resolveTranslationSubtypeId(translationType),
            Paid: false,
            RateType: rateType === "" ? null : rateType,
            Rate: rate === "" ? null : rate,
            UnitTypeID: normalizedUnitTypeId,
            UnitCount: normalizedUnitCount,
            UnitRate: unitRate === "" ? null : Number(unitRate),
            WordCount: legacyWordCount,
            Deleted: false,
            Cancelled: false,
            NumberOfLanguages: 1, // Single language per service
            DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
            Invoiced: false,
            CancellationReason: null,
            CreationSource: assignmentCreationSource,
            CreatedBy: currentUser.id,
            Checker: null,
            EmailThreadID: emailThreadIds.length ? emailThreadIds : null,
          },
          SourceLanguage: sourceLanguage,
          TargetLanguages: [targetLanguage], // Only this language
          Client: client,
          Requestor: requestor,
          Linguist: linguist,
          Invoicer: invoiceTo,
          CostLines: dynamicCostLines,
        };

        return api.post("/services/translations/addNewTranslation", {
          data: data,
        });
      });

      // Wait for all services to be created
      Promise.all(servicePromises)
        .then((responses) => {
          // Mark save complete and refresh UI immediately
          setIsSavingChanges(false);
          triggerPageRefresh(
            `${responses.length} Translations Added Successfully!`,
            theme,
          );

          // Emit assignment saved event for other viewers for each service
          if (socket) {
            responses.forEach((response) => {
              const projectNumberSafe = response.data.ProjectNumber || "";
              socket.emit("assignment:saved", {
                modelType: "Translation",
                recordId: response.data.ServiceID || response.data.id,
                projectNumber: projectNumberSafe,
                user: {
                  id: currentUser.id,
                  name: currentUser.Name,
                  imageThumbnail: currentUser.ImageThumbnail,
                },
              });
            });

            socket.emit("assignment:saved", {
              modelType: "TranslationsList",
              recordId: "all",
              serviceId:
                responses[0]?.data?.ServiceID || responses[0]?.data?.id || null,
              projectNumber: projectNumber,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          releaseLock();
          setOverlayName("");
        })
        .catch((err) => {
          console.log(err);
          setIsSavingChanges(false);
          setIsEditable(true);
        });

      return;
    }

    // Original behavior: create one service with all languages
    const data = {
      Assignment: {
        id: 0,
        Date: date.format("YYYY-MM-DD"),
        Deadline:
          deadline && deadline.isValid() ? deadline.format("YYYY-MM-DD") : null,
        DeliveryDate:
          deliveryDate && deliveryDate.isValid()
            ? deliveryDate.format("YYYY-MM-DD")
            : null,
        ProjectNumber: projectNumber,
        CheckingDate: null,
        ServiceCategory: "Translation",
        Name: name,
        Details: details,
        TranslationStatus: translationStatus === "" ? null : translationStatus,
        AccountCharge: accountCharge === "" ? null : accountCharge,
        // 2026-10-08 (stored-columns-only — supersedes the 2026-10-06 (a)
        // model): persist the DISPLAYED totals so Portal readers can read the
        // stored columns; empty field → null (unchanged).
        TotalCost: totalCost === "" ? null : totalCost,
        TotalLinguistPay:
          totalLinguistPay === ""
            ? null
            : Number(totalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          additionalLinguistCost === "" ? null : additionalLinguistCost,
        AdditionalCost: additionalCost === "" ? null : additionalCost,
        LinguistPay: linguistPay === "" ? null : linguistPay,
        NotarizationOrCOTCost:
          notarizationOrCOTCost === "" ? null : notarizationOrCOTCost,
        DeliveryCost: deliveryCost === "" ? null : deliveryCost,
        DeliveryType: deliveryType === "" ? null : deliveryType,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        PaymentType: paymentType === "" ? null : paymentType,
        Profit: profit === "" ? null : profit,
        Margin: margin === "" ? null : margin,
        TranslationType: translationType,
        ServiceSubtypeID: resolveTranslationSubtypeId(translationType),
        Paid: false,
        RateType: rateType === "" ? null : rateType,
        Rate: rate === "" ? null : rate,
        UnitTypeID: normalizedUnitTypeId,
        UnitCount: normalizedUnitCount,
        UnitRate: unitRate === "" ? null : Number(unitRate),
        WordCount: legacyWordCount,
        Deleted: false,
        Cancelled: false,
        NumberOfLanguages: targetLanguages.length,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        Invoiced: false,
        CancellationReason: null,
        CreationSource: assignmentCreationSource,
        CreatedBy: currentUser.id,
        Checker: null,
        EmailThreadID: emailThreadIds.length ? emailThreadIds : null,
      },
      SourceLanguage: sourceLanguage,
      TargetLanguages: targetLanguages,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
      CostLines: dynamicCostLines,
    };
    api
      .post("/services/translations/addNewTranslation", {
        data: data,
      })
      .then((response) => {
        const dataHistory = {
          Status: "Created",
          ServiceID: response.data.id,
          ProjectNumber: response.data.ProjectNumber,
          Date_FROM: null,
          Date_TO: response.data.Date,
          Deadline_FROM: null,
          Deadline_TO: response.data.Deadline,
          DeliveryDate_FROM: null,
          DeliveryDate_TO: response.data.DeliveryDate,
          ClientID_FROM: null,
          ClientID_TO: response.data.ClientID,
          RequestorID_FROM: null,
          RequestorID_TO: response.data.RequestorID,
          LinguistID_FROM: null,
          LinguistID_TO: response.data.LinguistID,
          ChangedBy: currentUser.id,
          Name_FROM: null,
          Name_TO: response.data.Name,
          Details_FROM: null,
          Details_TO: response.data.Details,
          TranslationStatus_FROM: null,
          TranslationStatus_TO: response.data.TranslationStatus,
          AccountCharge_FROM: null,
          AccountCharge_TO: response.data.AccountCharge,
          TotalCost_FROM: null,
          TotalCost_TO: response.data.TotalCost,
          AdditionalCost_FROM: null,
          AdditionalCost_TO: response.data.AdditionalCost,

          NotarizationOrCOTCost_FROM: null,
          NotarizationOrCOTCost_TO: response.data.NotarizationOrCOTCost,

          PaymentFees_FROM: null,
          PaymentFees_TO: response.data.PaymentFees,

          DeliveryCost_FROM: null,
          DeliveryCost_TO: response.data.DeliveryCost,

          PaymentType_FROM: null,
          PaymentType_TO: response.data.PaymentType,

          DeliveryType_FROM: null,
          DeliveryType_TO: response.data.DeliveryType,

          NumberOfLanguages_FROM: null,
          NumberOfLanguages_TO: response.data.NumberOfLanguages,
          LinguistPay_FROM: null,
          LinguistPay_TO: response.data.LinguistPay,
          Profit_FROM: null,
          Profit_TO: response.data.Profit,
          Margin_FROM: null,
          Margin_TO: response.data.Margin,
          TranslationType_FROM: null,
          TranslationType_TO: response.data.TranslationType,
          InvoiceTo_FROM: null,
          InvoiceTo_TO: response.data.InvoiceTo,
          Paid_FROM: null,
          Paid_TO: response.data.Paid,
          Rate_FROM: null,
          Rate_TO: response.data.Rate,
          RateType_FROM: null,
          RateType_TO: response.data.RateType,
          UnitTypeID_FROM: null,
          UnitTypeID_TO: response.data.UnitTypeID,
          UnitCount_FROM: null,
          UnitCount_TO: response.data.UnitCount,
          WordCount_FROM: null,
          WordCount_TO: response.data.WordCount,

          SourceLanguage_FROM: null,
          SourceLanguage_TO: sourceLanguage?.id ?? null,

          DisableAutoChargeCalc_FROM: null,
          DisableAutoChargeCalc_TO: response.data.DisableAutoChargeCalc,

          TargetLanguagesIDs_FROM: null,
          TargetLanguagesIDs_TO: targetLanguages,

          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };

        void dataHistory;

        // Mark save complete and refresh UI immediately
        setIsSavingChanges(false);
        triggerPageRefresh("New Translation Added Successfully!", theme, {
          label: "Open",
          onClick: () => navigate(`/translations/${response.data.id}`),
        });
        // Emit assignment saved event for other viewers
        if (socket) {
          const projectNumberSafe =
            projectNumber || response.data.ProjectNumber || "";
          socket.emit("assignment:saved", {
            modelType: "Translation",
            recordId: response.data.ServiceID || response.data.id,
            projectNumber: projectNumberSafe,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
          socket.emit("assignment:saved", {
            modelType: "TranslationsList",
            recordId: "all",
            serviceId: response.data.ServiceID || response.data.id,
            projectNumber: projectNumberSafe,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        releaseLock();
        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  function updateTranslation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const dynamicCostLines = buildDynamicCostLines();

    const normalizedUnitTypeId = unitTypeId === "" ? null : Number(unitTypeId);
    const normalizedUnitCount = unitCount === "" ? null : Number(unitCount);
    const legacyWordCount =
      unitTypeName === "words" ? normalizedUnitCount : null;

    const dataOld = {
      Assignment: {
        id: overlayData.id,
        Date: overlayData.Date,
        Deadline: overlayData.Deadline,
        ProjectNumber: overlayData.ProjectNumber,
        CheckingDate: null,
        ServiceCategory: "Translation",
        Name: overlayData.Name,
        Details: overlayData.Details,
        TranslationStatus:
          overlayData.TranslationStatus === ""
            ? null
            : overlayData.TranslationStatus,
        AccountCharge:
          overlayData.AccountCharge === "" ? null : overlayData.AccountCharge,
        TotalCost: overlayData.TotalCost === "" ? null : overlayData.TotalCost,
        TotalLinguistPay:
          overlayData.TotalLinguistPay === "" ||
          overlayData.TotalLinguistPay == null
            ? null
            : Number(overlayData.TotalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          overlayData.AdditionalLinguistCost === "" ||
          overlayData.AdditionalLinguistCost == null
            ? null
            : Number(overlayData.AdditionalLinguistCost).toFixed(2),
        AdditionalCost:
          overlayData.AdditionalCost === "" ||
          overlayData.AdditionalCost === null
            ? "0.00"
            : overlayData.AdditionalCost,
        LinguistPay:
          overlayData.LinguistPay === "" ? null : overlayData.LinguistPay,
        NotarizationOrCOTCost:
          overlayData.NotarizationOrCOTCost === ""
            ? null
            : overlayData.NotarizationOrCOTCost,
        DeliveryCost:
          overlayData.DeliveryCost === "" ? null : overlayData.DeliveryCost,
        DeliveryType:
          overlayData.DeliveryType === "" ? null : overlayData.DeliveryType,
        DeliveryDate: overlayData.DeliveryDate,
        PaymentFees:
          overlayData.PaymentFees === "" ? null : overlayData.PaymentFees,
        PaymentType:
          overlayData.PaymentType === "" ? null : overlayData.PaymentType,
        Profit: overlayData.Profit === "" ? null : overlayData.Profit,
        Margin: overlayData.Margin === "" ? null : overlayData.Margin,
        TranslationType:
          (overlayData.ServiceSubtype?.Name || "") === ""
            ? null
            : overlayData.ServiceSubtype?.Name,
        ServiceSubtypeID:
          overlayData.ServiceSubtypeID === "" || overlayData.ServiceSubtypeID == null
            ? null
            : Number(overlayData.ServiceSubtypeID),
        Paid: false,
        RateType: overlayData.RateType === "" ? null : overlayData.RateType,
        Rate: overlayData.Rate === "" ? null : overlayData.Rate,
        UnitRate: overlayData.UnitRate === "" ? null : overlayData.UnitRate,
        UnitTypeID:
          overlayData.UnitTypeID === ""
            ? null
            : overlayData.UnitTypeID || overlayData.UnitType?.id,
        UnitCount: overlayData.UnitCount === "" ? null : overlayData.UnitCount,
        WordCount: overlayData.WordCount === "" ? null : overlayData.WordCount,
        NumberOfLanguages:
          overlayData.NumberOfLanguages === ""
            ? null
            : overlayData.NumberOfLanguages,
        DisableAutoChargeCalc: overlayData.DisableAutoChargeCalc || false,
        Deleted: false,
        Cancelled: false,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        Checker: null,
        EmailThreadID: parseEmailThreadIds(overlayData.EmailThreadID),
      },
      SourceLanguage: overlayData.Language,
      TargetLanguages: overlayData.ServiceTargetLanguages,
      Client: overlayData.Client,
      Requestor: overlayData.Requestor,
      Linguist: overlayData.Linguist,
      Invoicer: overlayData.Invoicer,
    };

    const dataNew = {
      Assignment: {
        id: overlayData.id,
        Date: date.format("YYYY-MM-DD"),
        Deadline:
          deadline && deadline.isValid() ? deadline.format("YYYY-MM-DD") : null,
        ProjectNumber: projectNumber,
        //date.format("YYYY-MM-DD") === overlayData.Date
        //  ? overlayData.ProjectNumber
        //  : projectNumber,
        CheckingDate: null,
        ServiceCategory: "Translation",
        Name: name,
        Details: details,
        TranslationStatus: translationStatus === "" ? null : translationStatus,
        AccountCharge:
          accountCharge === "" ? null : Number(accountCharge).toFixed(2),
        // 2026-10-08 (stored-columns-only — supersedes the 2026-10-06 (a)
        // model): persist the DISPLAYED totals so Portal readers can read the
        // stored columns; empty field → null (unchanged).
        TotalCost:
          totalCost === "" ? null : Number(totalCost).toFixed(2),
        TotalLinguistPay:
          totalLinguistPay === ""
            ? null
            : Number(totalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          additionalLinguistCost === ""
            ? null
            : Number(additionalLinguistCost).toFixed(2),
        AdditionalCost:
          additionalCost === "" ? null : Number(additionalCost).toFixed(2),
        LinguistPay: linguistPay === "" ? null : Number(linguistPay).toFixed(2),
        NotarizationOrCOTCost:
          notarizationOrCOTCost === "" ? null : notarizationOrCOTCost,
        DeliveryCost: deliveryCost === "" ? null : deliveryCost,
        DeliveryDate:
          deliveryDate && deliveryDate.isValid()
            ? deliveryDate.format("YYYY-MM-DD")
            : null,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        PaymentType: paymentType === "" ? null : paymentType,
        DeliveryType: deliveryType === "" ? null : deliveryType,
        Profit: profit === "" ? null : Number(profit).toFixed(2),
        Margin: margin === "" ? null : Number(margin).toFixed(2),
        TranslationType: translationType,
        ServiceSubtypeID: resolveTranslationSubtypeId(translationType),
        Paid: false,
        RateType: rateType === "" ? null : rateType,
        Rate: rate === "" ? null : Number(rate).toFixed(2),
        UnitTypeID: normalizedUnitTypeId,
        UnitCount: normalizedUnitCount,
        UnitRate: unitRate === "" ? null : Number(unitRate),
        WordCount: legacyWordCount,
        NumberOfLanguages: targetLanguages.length,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        Deleted: false,
        Cancelled: false,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        Checker: null,
        EmailThreadID: emailThreadIds.length ? emailThreadIds : null,
      },
      SourceLanguage: sourceLanguage,
      TargetLanguages: targetLanguages,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
      CostLines: dynamicCostLines,
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/translations/updateTranslation", {
        data: allData,
      })
      .then((response) => {
        const historyData = {
          ...response.data,
          Status: "Updated",
          ChangedBy: currentUser.id,
          DisableAutoChargeCalc_FROM:
            overlayData.DisableAutoChargeCalc || false,
          DisableAutoChargeCalc_TO: disableAutomaticCalculationOfCharges,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };

        void historyData;

        // Mark save complete and refresh UI immediately
        setIsSavingChanges(false);
        triggerPageRefresh("Translation Assignment Updated Sucessfully", theme);

        // Emit assignment saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "Translation",
            recordId: overlayData.id,
            projectNumber: projectNumber,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
          socket.emit("assignment:saved", {
            modelType: "TranslationsList",
            recordId: "all",
            serviceId: overlayData.id,
            projectNumber: projectNumber,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        releaseLock();
        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  useEffect(() => {
    if (shouldValidate) {
      if (
        !(
          dateError ||
          projectNumberError ||
          nameError ||
          translationTypeError ||
          clientError ||
          requestorError ||
          SourceLanguageError ||
          linguistError ||
          rateError ||
          translationStatusError ||
          accountChargeError ||
          totalCostError ||
          linguistPayError ||
          detailsError ||
          emailThreadLinkError
        )
      ) {
        const invalidFlagRows = costRows.filter(
          (row) =>
            Number(getCostValueByType(row.typeKey) || 0) > 0 &&
            !row.includeInTotalCost &&
            !row.includeInLinguistPay &&
            !row.includeInAccountCharge,
        );

        if (invalidFlagRows.length > 0) {
          openDialog({
            theme,
            title: "Cost Line Inclusion Required",
            message:
              "Each cost line must be included in at least one total (Total Cost, Total Linguist Pay, or Account Charge).",
            primaryAction: "OK",
            primaryOnClick: closeDialog,
          });
        } else {
          isNewAssignment
            ? addNewTranslationRef.current()
            : updateTranslationRef.current();
        }
      }
      setShouldValidate(false);
    }
  }, [
    shouldValidate,
    nameError,
    dateError,
    projectNumberError,
    translationTypeError,
    clientError,
    requestorError,
    SourceLanguageError,
    linguistError,
    rateError,
    translationStatusError,
    accountChargeError,
    totalCostError,
    linguistPayError,
    detailsError,
    emailThreadLinkError,
    isNewAssignment,
    costRows,
    closeDialog,
    getCostValueByType,
    openDialog,
    theme,
  ]);

  useEffect(() => {
    updateCurrentProgressRef.current();
  }, [
    name,
    date,
    deadline,
    projectNumber,
    translationType,
    client,
    requestor,
    sourceLanguage,
    linguist,
    rate,
    rateType,
    unitTypeId,
    unitCount,
    translationStatus,
    targetLanguages,
    accountCharge,
    totalCost,
    linguistPay,
    details,
  ]);

  useEffect(() => {
    if (!isNewAssignment) {
      initialDataRef.current = {
        name: overlayData.Name,
        date: overlayData.Date, // assuming string format "YYYY-MM-DD"
        deadline: overlayData.Deadline,
        projectNumber: overlayData.ProjectNumber,
        translationType: overlayData.ServiceSubtype?.Name,
        client: overlayData.Client, // if client is an object, consider comparing by id
        requestor: overlayData.Requestor, // comparing by Name (or id if available)
        invoiceTo: overlayData.Invoicer,
        targetLanguages: overlayData.ServiceTargetLanguages,
        sourceLanguage: overlayData.Language,
        linguist: overlayData.Linguist,
        translationStatus: overlayData.TranslationStatus,
        accountCharge: overlayData.AccountCharge,
        totalCost: overlayData.TotalCost,
        linguistPay: overlayData.LinguistPay,
        additionalLinguistCost: overlayData.AdditionalLinguistCost,
        additionalCost: overlayData.AdditionalCost,
        unitTypeId: overlayData.UnitTypeID,
        unitCount: overlayData.UnitCount ?? overlayData.WordCount,
        rate: overlayData.Rate,
        emailThreadIDs: parseEmailThreadIds(overlayData.EmailThreadID).join("|"),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isNewAssignment && initialDataRef.current) {
      const languageList = (initialDataRef.current.targetLanguages || []).map(
        (targetLanguage) => targetLanguage.Language,
      );

      const dirty =
        name !== initialDataRef.current.name ||
        (date && date.format("YYYY-MM-DD")) !== initialDataRef.current.date ||
        (deadline && deadline.format("YYYY-MM-DD")) !==
          initialDataRef.current.deadline ||
        Number(projectNumber) !==
          Number(initialDataRef.current.projectNumber) ||
        translationType !== initialDataRef.current.translationType ||
        client?.Account !== initialDataRef.current.client?.Account ||
        requestor?.Name !== initialDataRef.current.requestor?.Name ||
        invoiceTo?.Name !== initialDataRef.current.invoiceTo?.Name ||
        JSON.stringify(targetLanguages) !== JSON.stringify(languageList) ||
        linguist?.Name !== initialDataRef.current.linguist?.Name ||
        sourceLanguage?.Name !== initialDataRef.current.sourceLanguage?.Name ||
        linguist?.Name !== initialDataRef.current.linguist?.Name ||
        translationStatus !== initialDataRef.current.translationStatus || //Error here
        Number(accountCharge) !==
          Number(initialDataRef.current.accountCharge) ||
        Number(totalCost) !== Number(initialDataRef.current.totalCost) ||
        Number(linguistPay) !== Number(initialDataRef.current.linguistPay) ||
        Number(additionalLinguistCost) !==
          Number(initialDataRef.current.additionalLinguistCost) ||
        Number(additionalCost) !==
          Number(initialDataRef.current.additionalCost) ||
        Number(unitTypeId || 0) !==
          Number(initialDataRef.current.unitTypeId || 0) ||
        (Number(unitCount) || 0) !==
          (Number(initialDataRef.current.unitCount) || 0) ||
        Number(rate) !== Number(initialDataRef.current.rate) ||
        emailThreadIds.join("|") !==
          (initialDataRef.current.emailThreadIDs || "");
      setHasChanges(dirty);
      // Mirror into the ref so the socket refetch handler sees current dirtiness.
      hasChangesRef.current = dirty;
    }
  }, [
    name,
    date,
    deadline,
    projectNumber,
    translationType,
    client,
    requestor,
    invoiceTo,
    targetLanguages,
    sourceLanguage,
    linguist,
    translationStatus,
    accountCharge,
    totalCost,
    linguistPay,
    additionalLinguistCost,
    additionalCost,
    unitTypeId,
    unitCount,
    rate,
    emailThreadIds,
    isNewAssignment,
  ]);

  const saveTranslation = () => {
    // Check if we need to show paid warning
    if (!pendingSave && checkPaidWarning()) {
      setShowPaidWarning(true);
      return;
    }

    // If we reach here, either no warning needed or user confirmed
    setPendingSave(false);
    validateFields();
    setShouldValidate(true);
  };

  const attemptSave = () => {
    if (isSavingChanges || isLocked) {
      return;
    }

    if (isNewAssignment && !triggerMissingFieldsShake()) {
      return;
    }

    if (isNewAssignment && projectNumberExists) {
      openDialog({
        theme,
        title: "Duplicate Project Number",
        message: `The project number ${projectNumber} already exists. Create another assignment with the same number?`,
        primaryAction: "Create Anyway",
        secondaryAction: "Cancel",
        primaryOnClick: () => {
          closeDialog();
          saveTranslation();
        },
        secondaryOnClick: closeDialog,
      });
      return;
    }

    saveTranslation();
  };

  // Handle confirmation of paid assignment warning
  const handlePaidWarningConfirm = () => {
    setShowPaidWarning(false);
    setPendingSave(true);
    // Trigger save again, but this time with pendingSave = true to bypass the check
    setTimeout(() => saveTranslation(), 0);
  };

  // Handle cancellation of paid assignment warning
  const handlePaidWarningCancel = () => {
    setShowPaidWarning(false);
    setPendingSave(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');
      const isInsidePopup =
        target instanceof Element &&
        (!!target.closest('[role="listbox"]') ||
          !!target.closest(".MuiAutocomplete-popper") ||
          !!target.closest(".MuiPopover-root") ||
          !!target.closest(".MuiMenu-root"));

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog &&
        !isInsidePopup
      ) {
        e.preventDefault();
        attemptSaveRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  addNewTranslationRef.current = addNewTranslation;
  updateTranslationRef.current = updateTranslation;
  updateCurrentProgressRef.current = updateCurrentProgress;
  saveTranslationRef.current = saveTranslation;
  attemptSaveRef.current = attemptSave;

  return (
    <>
    <Overlay
      id="translation-new-edit"
      open={true}
      onClose={handleBackButtonClick}
      title={
        isNewAssignment
          ? "New Translation Assignment"
          : completing
            ? "Complete Translation Assignment"
            : "Edit Translation Assignment"
      }
      warnings={[
        ...(paidLocked
          ? [
              {
                type: "error",
                message:
                  "Editing is disabled for services on a paid invoice. Un-mark the invoice as paid first to edit this service.",
              },
            ]
          : []),
      ]}
      backButtonProps={{
        onMouseDown: handleBackButtonPressStart,
        onMouseUp: handleBackButtonPressEnd,
        onMouseLeave: handleBackButtonPressEnd,
        onTouchStart: handleBackButtonPressStart,
        onTouchEnd: handleBackButtonPressEnd,
        onTouchCancel: handleBackButtonPressEnd,
      }}
      footer={
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "50%",
              flexDirection: "row",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color:
                  progress < 50
                    ? theme.palette.error[600]
                    : progress < 100
                      ? theme.palette.warning[500]
                      : theme.palette.primary.main,
              }}
            >
              {progressMessage}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "50%",
              flexDirection: "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Discard">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      releaseLock();
                      setOverlayName("");
                    }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Save">
                  <IconButton
                    onClick={attemptSave}
                    disabled={isSavingChanges || isLocked || paidLocked}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.grey[400],
                        color: theme.palette.grey[200],
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => {
                    releaseLock();
                    setOverlayName("");
                  }}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>

                <Box p="0 .5rem" />

                <Button
                  loading={isSavingChanges}
                  disabled={isSavingChanges || isLocked || paidLocked}
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<SaveRoundedIcon />}
                  type="submit"
                  onClick={attemptSave}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        </>
      }
    >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          component="form"
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
              height: "90%",
            },
          }}
        >
          <Grid container spacing={2} width="100%">
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <CEDatePicker
                  disabled={!isEditable}
                  id={"Date"}
                  label="Date"
                  size="small"
                  format="dddd, MMMM DD, YYYY"
                  value={date}
                  onChange={(newValue) => {
                    setDate(newValue);
                    setDateError(newValue === null);
                  }}
                  sx={{
                    ...getShakeSx("Date"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (date && date.format("YYYY-MM-DD")) || "",
                          overlayData?.Date || "",
                        ),
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: dateError,
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <CEDatePicker
                  disabled={!isEditable}
                  id={"Deadline"}
                  label="Deadline"
                  size="small"
                  format="dddd, MMMM DD, YYYY"
                  value={deadline}
                  onChange={(newValue) => {
                    setDeadline(newValue);
                    setDeadlineError(newValue === null);
                  }}
                  sx={{
                    ...getShakeSx("Deadline"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (deadline && deadline.format("YYYY-MM-DD")) || "",
                          overlayData.Deadline,
                        ),
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: dateError,
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"ProjectNumber"}
                  label="Project Number"
                  error={projectNumberError || projectNumberExists}
                  value={projectNumber}
                  onChange={(event) => {
                    setProjectNumber(event.target.value);
                    setProjectNumberError(event.target.value === null);
                    setProjectNumberExists(false);
                  }}
                  onBlur={async () => {
                    if (projectNumber && isNewAssignment) {
                      try {
                        const response = await api.get(
                          `/services/check-project-number?projectNumber=${projectNumber}`,
                        );
                        setProjectNumberExists(response.data.exists);
                      } catch (error) {
                        console.error("Error checking project number:", error);
                      }
                    }
                  }}
                  helperText={
                    projectNumberExists
                      ? "This project number already exists (Ignore if duplicate)."
                      : ""
                  }
                  sx={{
                    ...getShakeSx("ProjectNumber"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: projectNumberExists
                          ? theme.palette.warning.main
                          : getBorderColor(
                              theme,
                              isNewAssignment,
                              Number(projectNumber) || "",
                              Number(overlayData.ProjectNumber)
                            ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"TranslationType"}
                  label="Translation Type"
                  error={translationTypeError}
                  value={translationType}
                  onChange={(event) => {
                    setTranslationType(event.target.value);
                    setTranslationTypeError(event.target.value === null);

                    fillDetails(
                      requestor.Name,
                      name,
                      Number(unitCount),
                      sourceLanguage?.Name || "",
                      targetLanguages,
                      event.target.value,
                    );
                  }}
                  sx={{
                    ...getShakeSx("TranslationType"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          translationType || "",
                          overlayData.TranslationType,
                        ),
                      },
                    },
                  }}
                >
                  {overlayData.TranslationTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Name"}
                label="Name"
                error={nameError}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(event.target.value === null);

                  fillDetails(
                    requestor.Name,
                    event.target.value,
                    Number(unitCount),
                    sourceLanguage?.Name || "",
                    targetLanguages,
                    translationType,
                  );
                }}
                sx={{
                  ...getShakeSx("Name"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        name || "",
                        overlayData.Name,
                      ),
                    },
                  },
                }}
              />
            </Grid>

            {completing ? null : null}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Language"}
                  size="small"
                  options={languageOptions}
                  getOptionLabel={(language) => language?.Name || ""}
                  renderOption={renderTranslationLanguageOption}
                  value={sourceLanguage}
                  onChange={(event, newValue) => {
                    let selectedSourceLanguage;

                    if (typeof newValue === "string") {
                      selectedSourceLanguage = {
                        Name: newValue,
                      };
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      selectedSourceLanguage = {
                        Name: newValue.inputValue,
                      };
                    } else {
                      selectedSourceLanguage = newValue;
                    }

                    setSourceLanguage(selectedSourceLanguage);
                    applyTargetLanguages(
                      targetLanguages,
                      selectedSourceLanguage,
                    );

                    setSourceLanguageError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source Language"
                      placeholder=""
                      error={SourceLanguageError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("SourceLanguage"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (sourceLanguage && sourceLanguage.Name) || "",
                          (overlayData &&
                            overlayData.Language &&
                            overlayData.Language.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Client"}
                  size="small"
                  options={overlayData.Clients}
                  getOptionLabel={(client) => client?.Account || ""}
                  value={client}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setClient({
                        Account: newValue,
                      });
                      setAddNewClient(false);
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setClient({
                        Account: newValue.inputValue,
                      });

                      setAddNewClient(true);
                    } else {
                      setClient(newValue);
                      setAddNewClient(false);
                    }

                    setRequestor("");

                    setClientError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const { inputValue } = params;

                    // Filter by both Account and ShortName
                    const filtered = options.filter((option) => {
                      const accountMatch =
                        option.Account?.toLowerCase().includes(
                          inputValue.toLowerCase(),
                        );
                      const shortNameMatch =
                        option.ShortName?.toLowerCase().includes(
                          inputValue.toLowerCase(),
                        );
                      return accountMatch || shortNameMatch;
                    });

                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) =>
                        inputValue === option.Account ||
                        inputValue === option.ShortName,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Account: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      placeholder=""
                      error={clientError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Client"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (client && client.Account) || "",
                          (overlayData.Client && overlayData.Client.Account) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Requestor"}
                  size="small"
                  id={"Requestor"}
                  options={(client && client.Requestors) || []}
                  getOptionLabel={(requestor) => requestor?.Name || ""}
                  value={requestor}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      const nextRequestor = { Name: newValue };
                      setRequestor(nextRequestor);
                      setAddNewRequestor(false);
                      // Default the invoice recipient to the requestor.
                      applyRequestorInvoiceDefaults(nextRequestor);
                      fillDetails(
                        newValue,
                        name,
                        Number(unitCount),
                        sourceLanguage?.Name || "",
                        targetLanguages,
                        translationType,
                      );
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      const nextRequestor = { Name: newValue.inputValue };
                      setRequestor(nextRequestor);
                      setAddNewRequestor(true);
                      // Default the invoice recipient to the requestor.
                      applyRequestorInvoiceDefaults(nextRequestor);
                      fillDetails(
                        newValue.inputValue,
                        name,
                        Number(unitCount),
                        sourceLanguage?.Name || "",
                        targetLanguages,
                        translationType,
                      );
                    } else {
                      setRequestor(newValue);
                      setAddNewRequestor(false);
                      // Auto-populate invoice recipient + CC from the
                      // requestor's group (or default to the requestor).
                      applyRequestorInvoiceDefaults(newValue);
                      fillDetails(
                        newValue.Name,
                        name,
                        Number(unitCount),
                        sourceLanguage?.Name || "",
                        targetLanguages,
                        translationType,
                      );
                    }

                    setRequestorError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Requestor"
                      placeholder=""
                      error={requestorError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Requestor"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (requestor && requestor.Name) || "",
                          (overlayData &&
                            overlayData.Requestor &&
                            overlayData.Requestor.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Linguist"}
                  size="small"
                  options={linguistOptions}
                  getOptionLabel={(linguist) => linguist?.Name || ""}
                  isOptionEqualToValue={(option, value) => {
                    const optionId = Number(option?.id || 0);
                    const valueId = Number(value?.id || 0);

                    if (optionId > 0 && valueId > 0) {
                      return optionId === valueId;
                    }

                    return (option?.Name || "") === (value?.Name || "");
                  }}
                  value={linguist}
                  renderOption={renderLinguistOption}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setLinguist({
                        Name: newValue,
                      });
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setLinguist({
                        Name: newValue.inputValue,
                      });
                    } else {
                      setLinguist(newValue);
                    }

                    setLinguistError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value — EXCEPT "N/A":
                    // N/A is the reserved placeholder linguist (canonical id 1)
                    // and must never be created (Amine: don't show "Add N/A").
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (
                      inputValue !== "" &&
                      !isExisting &&
                      inputValue.trim().toUpperCase() !== "N/A"
                    ) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Linguist"
                      placeholder=""
                      error={linguistError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Linguist"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          linguist.Name || "",
                          (overlayData.Linguist && overlayData.Linguist.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                disabled={!isEditable}
                disableClearable
                key={"InvoiceTo"}
                size="small"
                id={"InvoiceTo"}
                options={(client && client.Requestors) || []}
                getOptionLabel={(requestor) => requestor?.Name || ""}
                value={invoiceTo}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    setInvoiceTo({
                      Name: newValue,
                    });
                    setAddNewInvoiceTo(false);
                  } else if (newValue && newValue.inputValue) {
                    // Create a new value from the user input
                    setInvoiceTo({
                      Name: newValue.inputValue,
                    });
                    setAddNewInvoiceTo(true);
                  } else {
                    setInvoiceTo(newValue);
                    setAddNewInvoiceTo(false);
                  }

                  setInvoiceToError(newValue === null);
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some(
                    (option) => inputValue === option.Name,
                  );
                  if (inputValue !== "" && !isExisting) {
                    filtered.push({
                      inputValue,
                      Name: `Add "${inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                selectOnFocus
                clearOnBlur
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Invoice To"
                    placeholder=""
                    error={invoiceToError}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.MuiInputBase-root fieldset": {
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        (invoiceTo && invoiceTo.Name) || "",
                        (overlayData.Invoicer && overlayData.Invoicer.Name) ||
                          "",
                        true,
                      ),
                    },
                  },
                }}
              />
              {ccRequestors.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: theme.palette.text.secondary,
                  }}
                >
                  CC: {ccRequestors.map((cc) => cc?.Name).join(", ")}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id={"RateTypes"}
                label="Rate Types"
                error={rateTypeError}
                value={rateType}
                onChange={(event) => {
                  setRateType(event.target.value);
                  setRateTypeError(event.target.value === null);

                  switch (event.target.value) {
                    case "Rushed Translation":
                      setRate(0.25);
                      break;
                    case "Translation":
                      setRate(0.2);
                      break;
                    case "Custom":
                      setRate(0);
                      break;
                    case "Voiceover":
                      setRate(0);
                      break;
                    case "Design":
                      setRate(0);
                      break;
                    case "DTP":
                      setRate(0);
                      break;
                    default:
                      setRate(0);
                      break;
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        rateType || "",
                        overlayData.RateType,
                      ),
                    },
                  },
                }}
              >
                {overlayData.RateTypes.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Translation Status moved into the old Rate slot (2026-10-01,
                per Amine): the legacy Rate field is removed — "Unit Rate"
                is the rate. The hidden `rate` state remains the backward-
                compatible fallback for older assignments. */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id={"TranslationStatus"}
                label="Translation Status"
                error={translationStatusError}
                //defaultValue={translationStatus === null ? "Requested" : ""}
                value={translationStatus}
                onChange={(event) => {
                  setTranslationStatus(event.target.value);
                  setTranslationStatusError(event.target.value === null);
                }}
                sx={{
                  ...getShakeSx("TranslationStatus"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        translationStatus || "",
                        overlayData.TranslationStatus,
                      ),
                    },
                  },
                }}
              >
                {overlayData.TranslationStatuses.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Unit Type / Unit Count / Unit Rate span the full row width. */}
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"UnitType"}
                  label="Unit Type"
                  value={unitTypeId}
                  onChange={(event) => {
                    setUnitTypeId(Number(event.target.value));
                    fillDetails(
                      requestor.Name,
                      name,
                      Number(unitCount),
                      sourceLanguage?.Name || "",
                      targetLanguages,
                      translationType,
                    );
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 160,
                    ...getShakeSx("UnitType"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          unitTypeId || "",
                          overlayData.UnitTypeID || "",
                        ),
                      },
                    },
                  }}
                >
                  {translationUnitTypes.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>
                      {unit.Name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"UnitCount"}
                  label="Unit Count"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {unitTypeAbbrev}
                        </InputAdornment>
                      ),
                    },
                  }}
                  error={unitCountError}
                  value={unitCount}
                  onChange={(event) => {
                    setUnitCount(event.target.value);
                    setUnitCountError(event.target.value === null);

                    fillDetails(
                      requestor.Name,
                      name,
                      Number(event.target.value),
                      sourceLanguage?.Name || "",
                      targetLanguages,
                      translationType,
                    );
                  }}
                  sx={{
                    flex: 1,
                    ...getShakeSx("UnitCount"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(unitCount) || "",
                          Number(
                            overlayData.UnitCount ?? overlayData.WordCount,
                          ) || "",
                        ),
                      },
                    },
                  }}
                />

                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"UnitRate"}
                  label="Unit Rate"
                  error={rateError}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {`Per ${unitTypeAbbrev}`}
                        </InputAdornment>
                      ),
                    },
                  }}
                  value={unitRate}
                  onChange={(event) => {
                    setUnitRate(event.target.value);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 140,
                    ...getShakeSx("UnitRate"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(unitRate) || "",
                          // Effective original (2026-10-05): on rows whose
                          // UnitRate is NULL the field displays the legacy
                          // Rate fallback — compare against the same source,
                          // otherwise the fallback looked like an unsaved
                          // change (blue border) on open.
                          Number(overlayData.UnitRate ?? overlayData.Rate) || "",
                        ),
                      },
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                slots={{
                  popper: CustomPopper, // Use the custom Popper component
                }}
                multiple
                size="small"
                id="checkboxes-tags-demo"
                options={languageOptions.filter(
                  (lang) => !sourceLanguage || lang.id !== sourceLanguage.id,
                )}
                disableCloseOnSelect
                getOptionLabel={(option) => option.Name}
                //getoptionselected={(option, value) => option.id === value.id} // Add this line
                value={targetLanguages}
                onChange={(event, newValue) => {
                  applyTargetLanguages(
                    newValue.map((item) => ({
                      ...item,
                    })),
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  const isSelected = targetLanguages.some(
                    (lang) => lang.id === option.id,
                  ); // Check if the option is selected
                  const stats =
                    languageClientStatsById[Number(option?.id || 0)] ||
                    EMPTY_LANGUAGE_CLIENT_STATS;

                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={isSelected}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <Box component="span" sx={{ minWidth: 0 }}>
                          {option.Name}
                        </Box>
                        {hasSelectedClient ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
                          >
                            {`Translations: ${stats.requestCount}`}
                          </Typography>
                        ) : null}
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      !targetLanguages || targetLanguages.length === 0
                        ? "Target Languages"
                        : "Target Languages: " + targetLanguages.length
                    }
                    placeholder="Type or select from the list"
                    sx={{
                      ...getShakeSx("TargetLanguages"),
                      "& .MuiOutlinedInput-root": {
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,

                            (targetLanguages && targetLanguages.length > 0) ||
                              "",
                            (overlayData.ServiceTargetLanguages &&
                              overlayData.ServiceTargetLanguages.length > 0) ||
                              "",
                          ),
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    mb: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2">Costs</Typography>
                  <IconButton
                    size="small"
                    disabled={
                      !isEditable ||
                      costRows.length >=
                        costTypeOptions.filter((option) => option.isEnabled).length
                    }
                    onClick={addCostRow}
                    sx={{ p: 0.5 }}
                  >
                    <AddRoundedIcon fontSize="small" />
                  </IconButton>
                  <Divider sx={{ flex: 1, ml: 0.5 }} />
                </Box>
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts && costRows.length > 0 ? (
              <Grid container spacing={2} size={{ xs: 12 }}>
                {costRows.map((row) => {
                  const usedKeys = new Set(
                    costRows
                      .filter((item) => item.id !== row.id)
                      .map((item) => item.typeKey),
                  );
                  const isLinguistPayRow = row.typeKey === "linguistPay";
                  const isPaymentFeesRow = row.typeKey === "paymentFees";
                  const inclusionChanged =
                    row.includeInTotalCost !== row.originalIncludeInTotalCost ||
                    row.includeInLinguistPay !== row.originalIncludeInLinguistPay ||
                    row.includeInAccountCharge !== row.originalIncludeInAccountCharge;
                  const checkedFlagCount = [
                    row.includeInTotalCost,
                    row.includeInLinguistPay,
                    row.includeInAccountCharge,
                  ].filter(Boolean).length;
                  const isLastCheckedFlag = (flag) =>
                    Boolean(flag) && checkedFlagCount === 1;

                  return (
                    <Grid size={{ xs: 12 }} key={`cost-row-${row.id}`}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, md: "grow" }}>
                              <TextField
                                disabled={!isEditable}
                                select
                                size="small"
                                label="Cost Type"
                                value={row.typeKey}
                                onChange={(event) =>
                                  updateCostRowType(row.id, event.target.value)
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "&.MuiInputBase-root fieldset": {
                                      py: 0,
                                      borderColor: getBorderColor(
                                        theme,
                                        isNewAssignment,
                                        row.typeKey || "",
                                        getOriginalCostTypeByRowId(row.id) || "",
                                        true,
                                      ),
                                    },
                                  },
                                }}
                              >
                                {costTypeOptions
                                  .filter(
                                    (option) =>
                                      option.key === row.typeKey ||
                                      (option.isEnabled && !usedKeys.has(option.key)),
                                  )
                                  .map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: "grow" }}>
                              <TextField
                                disabled={!isEditable}
                                size="small"
                                label="Amount"
                                value={getCostValueByType(row.typeKey)}
                                onChange={(event) =>
                                  setCostValueByType(row.typeKey, event.target.value)
                                }
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">$</InputAdornment>
                                    ),
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "&.MuiInputBase-root fieldset": {
                                      py: 0,
                                      borderColor: getBorderColor(
                                        theme,
                                        isNewAssignment,
                                        Number(getCostValueByType(row.typeKey)) || "",
                                        Number(getOriginalCostValueByType(row.typeKey)) || "",
                                        true,
                                      ),
                                    },
                                  },
                                }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: "grow" }}>
                              <TextField
                                select
                                disabled={!isEditable}
                                size="small"
                                label="Include in"
                                slotProps={{
                                  select: {
                                    multiple: true,
                                    value: [
                                      ...(row.includeInTotalCost
                                        ? ["includeInTotalCost"]
                                        : []),
                                      ...(isLinguistPayRow ||
                                      row.includeInLinguistPay
                                        ? ["includeInLinguistPay"]
                                        : []),
                                      ...(row.includeInAccountCharge
                                        ? ["includeInAccountCharge"]
                                        : []),
                                    ],
                                    onChange: (event) => {
                                      const raw = event.target.value;
                                      const value = Array.isArray(raw)
                                        ? raw
                                        : raw == null
                                          ? []
                                          : [raw];
                                      if (value.length === 0) return;
                                      const next = new Set(value);
                                      if (
                                        row.includeInTotalCost !==
                                        next.has("includeInTotalCost")
                                      ) {
                                        updateCostRowFlag(
                                          row.id,
                                          "includeInTotalCost",
                                          next.has("includeInTotalCost"),
                                        );
                                      }
                                      if (
                                        !isLinguistPayRow &&
                                        row.includeInLinguistPay !==
                                          next.has("includeInLinguistPay")
                                      ) {
                                        updateCostRowFlag(
                                          row.id,
                                          "includeInLinguistPay",
                                          next.has("includeInLinguistPay"),
                                        );
                                      }
                                      if (
                                        row.includeInAccountCharge !==
                                        next.has("includeInAccountCharge")
                                      ) {
                                        updateCostRowFlag(
                                          row.id,
                                          "includeInAccountCharge",
                                          next.has("includeInAccountCharge"),
                                        );
                                      }
                                    },
                                    renderValue: (selected) =>
                                      selected.length === 3
                                        ? "All"
                                        : selected
                                            .map(
                                              (v) =>
                                                ({
                                                  includeInTotalCost:
                                                    "Total Cost",
                                                  includeInLinguistPay:
                                                    "Total Linguist Pay",
                                                  includeInAccountCharge:
                                                    "Account Charge",
                                                })[v],
                                            )
                                            .filter(Boolean)
                                            .join(", "),
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "&.MuiInputBase-root fieldset": {
                                      py: 0,
                                      borderColor: getBorderColor(
                                        theme,
                                        isNewAssignment,
                                        inclusionChanged ? "changed" : "unchanged",
                                        "unchanged",
                                      ),
                                    },
                                  },
                                }}
                              >
                                <MenuItem
                                  value="includeInTotalCost"
                                  disabled={
                                    !isEditable ||
                                    isLinguistPayRow ||
                                    isLastCheckedFlag(row.includeInTotalCost)
                                  }
                                >
                                  <Checkbox
                                    size="small"
                                    checked={Boolean(row.includeInTotalCost)}
                                    disabled={
                                      !isEditable ||
                                      isLinguistPayRow ||
                                      isLastCheckedFlag(row.includeInTotalCost)
                                    }
                                  />
                                  Total Cost
                                </MenuItem>
                                <MenuItem
                                  value="includeInLinguistPay"
                                  disabled={!isEditable || isLinguistPayRow}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      isLinguistPayRow
                                        ? true
                                        : Boolean(row.includeInLinguistPay)
                                    }
                                    disabled={!isEditable || isLinguistPayRow}
                                  />
                                  Total Linguist Pay
                                </MenuItem>
                                <MenuItem
                                  value="includeInAccountCharge"
                                  disabled={
                                    !isEditable ||
                                    isLinguistPayRow ||
                                    isPaymentFeesRow ||
                                    isLastCheckedFlag(
                                      row.includeInAccountCharge,
                                    )
                                  }
                                >
                                  <Checkbox
                                    size="small"
                                    checked={Boolean(
                                      row.includeInAccountCharge,
                                    )}
                                    disabled={
                                      !isEditable ||
                                      isLinguistPayRow ||
                                      isPaymentFeesRow ||
                                      isLastCheckedFlag(
                                        row.includeInAccountCharge,
                                      )
                                    }
                                  />
                                  Account Charge
                                </MenuItem>
                              </TextField>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box sx={{ flex: "0 0 auto", mt: { xs: 0, md: 0.5 } }}>
                          <IconButton
                            disabled={!isEditable}
                            onClick={() => removeCostRow(row.id)}
                            size="small"
                          >
                            <ClearRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts &&
            (costRows.some((row) => row.typeKey === "linguistPay" || row.typeKey === "additionalLinguistCost") ||
              Number(totalLinguistPay || 0) !== 0) ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalLinguistPay"}
                  label="Total Linguist Pay"
                  helperText={
                    <CostFormulaHelper parts={totalLinguistPayHelperParts} />
                  }
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  value={totalLinguistPay}
                  onChange={(event) => {
                    // 2026-10-06 (a) model: a non-empty entry is a user
                    // override (this field only — never cross-writes);
                    // clearing drops the override and the live calculation
                    // immediately re-fills the field.
                    const nextValue = event.target.value;
                    totalLinguistPayOverriddenRef.current = nextValue !== "";
                    if (nextValue === "") {
                      setTotalLinguistPay(getManualTlPayLiveValue());
                    } else {
                      setTotalLinguistPay(nextValue);
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(totalLinguistPay) || "",
                          Number(overlayData.TotalLinguistPay),
                          !completing,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <CEDatePicker
                  disabled={!isEditable}
                  id={"DeliveryDate"}
                  label="Delivery Date"
                  size="small"
                  format="dddd, MMMM DD, YYYY"
                  value={deliveryDate}
                  onChange={(newValue) => {
                    setDeliveryDate(newValue);
                    setDeliveryDateError(newValue === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (deliveryDate && deliveryDate.format("YYYY-MM-DD")) ||
                            "",
                          overlayData.DeliveryDate,
                        ),
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: deliveryDateError,
                    },
                  }}
                />
              </Grid>
            ) : null}

            {completing ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"PaymentType"}
                  label="Payment Type"
                  select
                  error={paymentTypeError}
                  value={paymentType || ""}
                  onChange={(event) => {
                    setPaymentType(event.target.value);
                    setPaymentTypeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          paymentType || "",
                          overlayData.PaymentType || "",
                        ),
                      },
                    },
                  }}
                >
                  {(overlayData.PaymentTypes || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {completing ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"DeliveryType"}
                  label="Delivery Type"
                  select
                  error={deliveryTypeError}
                  value={deliveryType || ""}
                  onChange={(event) => {
                    setDeliveryType(event.target.value);
                    setDeliveryTypeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          deliveryType || "",
                          overlayData.DeliveryType || "",
                        ),
                      },
                    },
                  }}
                >
                  {(overlayData.DeliveryTypes || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalCost"}
                  label="Total Cost"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  helperText={
                    disableAutomaticCalculationOfCharges ? null : (
                      <CostFormulaHelper parts={totalCostHelperParts} />
                    )
                  }
                  error={totalCostError}
                  value={totalCost}
                  onChange={(event) => {
                    // 2026-10-06 (a) model: a non-empty entry is a user
                    // override (this field only — never cross-writes);
                    // clearing drops it and the live calculation re-fills.
                    const nextValue = event.target.value;
                    totalCostOverriddenRef.current = nextValue !== "";
                    if (nextValue === "") {
                      setTotalCost(computeLiveManualTotals().totalCost);
                    } else {
                      setTotalCost(nextValue);
                    }
                    setTotalCostError(nextValue === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          Number(totalCost) || "",
                          Number(overlayData.TotalCost),
                          !completing,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}

            <Grid
              size={
                showLinguistPayAndOtherCosts ? { xs: 12, md: 6 } : { xs: 12 }
              }
            >
              <TextField
                disabled={!isEditable}
                size="small"
                id={"AccountCharge"}
                label="Account Charge"
                slotProps={{
                  input: {
                    readOnly: !disableAutomaticCalculationOfCharges,
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                helperText={
                  disableAutomaticCalculationOfCharges ? null : (
                    <CostFormulaHelper parts={accountChargeHelperParts} />
                  )
                }
                error={accountChargeError}
                value={accountCharge}
                onChange={(event) => {
                  setAccountCharge(event.target.value);
                  setAccountChargeError(event.target.value === null);

                  const accountChargeValue = parseFloat(event.target.value);
                  const totalCostValue = parseFloat(totalCost);

                  // Prevent division by zero and handle invalid values
                  if (
                    !event.target.value ||
                    accountChargeValue === 0 ||
                    isNaN(accountChargeValue) ||
                    isNaN(totalCostValue)
                  ) {
                    setProfit(0);
                    setMargin(0);
                    return;
                  }

                  const prof = accountChargeValue - totalCostValue;
                  setProfit(prof.toFixed(2));
                  const calculatedMargin = (prof / accountChargeValue) * 100;
                  setMargin(
                    isNaN(calculatedMargin) || !isFinite(calculatedMargin)
                      ? 0
                      : calculatedMargin.toFixed(2),
                  );
                }}
                sx={{
                  ...getShakeSx("AccountCharge"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        Number(accountCharge) || "",
                        Number(overlayData.AccountCharge),
                      ),
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={isEditable ? !allowDetailsEditing : true}
                size="small"
                id={"Details"}
                label="Details"
                error={detailsError}
                value={details}
                onChange={(event) => {
                  setDetails(event.target.value);
                  setDetailsError(event.target.value === null);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={true}
                size="small"
                id={"Profit"}
                label="Profit"
                value={profit}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor:
                        details === null || details === ""
                          ? "gray"
                          : theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={true}
                size="small"
                id={"Margin"}
                label="Margin"
                value={margin}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor:
                        details === null || details === ""
                          ? "gray"
                          : theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Grid>

            {/* Email Thread Links (2026-10-07, per Amine — Costs-style
                section): title + "+" button + divider header, then the link
                fields, then a divider, then the next section. Translations
                support the same MULTIPLE Outlook links as interpretations;
                only the id segments are saved. Non-empty rows must be valid
                Outlook links to save. */}
            {!completing ? (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    mb: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2">
                    Email Thread Links
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleAddEmailThreadLink}
                    disabled={!isEditable}
                    sx={{ p: 0.5 }}
                  >
                    <AddRoundedIcon fontSize="small" />
                  </IconButton>
                  <Divider sx={{ flex: 1, ml: 0.5 }} />
                </Box>
              </Grid>
            ) : null}

            {!completing ? (
              <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12 }}>
                  <Stack spacing={1} useFlexGap>
                    {emailThreadLinks.map((link, index) => {
                      const status = emailThreadLinkStatuses[index] || {
                        status: "empty",
                      };
                      const rowInvalid = status.status === "invalid";
                      const rowChanged =
                        status.status === "valid" &&
                        !originalEmailThreadIds.includes(status.id);
                      return (
                        <Box
                          key={`email-thread-link-row-${index}`}
                          sx={{
                            // Extra space BETWEEN consecutive link fields
                            // only — the editor's form-level
                            // `.MuiTextField-root { m: 0 }` rule nullifies
                            // margins on the fields themselves, so the
                            // spacing lives on this wrapper row.
                            mb: index < emailThreadLinks.length - 1 ? "8px" : 0,
                          }}
                        >
                        <TextField
                          disabled={!isEditable}
                          size="small"
                          id={`EmailThreadLink-${index}`}
                          label={
                            emailThreadLinks.length > 1
                              ? `Email Thread Link ${index + 1}`
                              : "Email Thread Link"
                          }
                          placeholder="Paste the Outlook email thread link"
                          value={link}
                          error={rowInvalid}
                          helperText={
                            rowInvalid
                              ? "Enter a valid Outlook email thread link (or the thread ID)."
                              : ""
                          }
                          onChange={(event) =>
                            handleEmailThreadLinkChange(index, event.target.value)
                          }
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {status.status === "valid" ? (
                                    <Tooltip title="Valid Outlook link">
                                      <CheckCircleRoundedIcon
                                        fontSize="small"
                                        color="success"
                                      />
                                    </Tooltip>
                                  ) : null}
                                  <Tooltip title="Paste link from clipboard">
                                    <span>
                                      <IconButton
                                        size="small"
                                        edge="end"
                                        disabled={!isEditable}
                                        onClick={() =>
                                          handlePasteEmailThreadLink(index)
                                        }
                                      >
                                        <ContentPasteRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  {emailThreadLinks.length > 1 ? (
                                    <Tooltip title="Remove this link">
                                      <span>
                                        <IconButton
                                          size="small"
                                          edge="end"
                                          disabled={!isEditable}
                                          onClick={() =>
                                            handleRemoveEmailThreadLink(index)
                                          }
                                        >
                                          <ClearRoundedIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  ) : null}
                                </InputAdornment>
                              ),
                            },
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              padding: "8.5px 12px !important",
                            },
                            "& .MuiOutlinedInput-root": {
                              py: 0,
                              "&.MuiInputBase-root fieldset": {
                                borderColor: rowInvalid
                                  ? theme.palette.error.main
                                  : rowChanged
                                    ? theme.palette.info[400]
                                    : status.status === "valid"
                                      ? theme.palette.primary.main
                                      : theme.palette.grey.main,
                              },
                            },
                          }}
                        />
                        </Box>
                      );
                    })}
                  </Stack>
                </Grid>
              </Grid>
            ) : null}

            {/* Divider between the thread links and the next section
                (Costs-style sectioned layout). */}
            {!completing ? (
              <Grid size={{ xs: 12 }}>
                <Divider />
              </Grid>
            ) : null}

            <Grid container size={{ xs: 12 }} spacing={1.5} sx={{ pb: 1 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Options
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: "10px", height: "100%" }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Manual Overrides
                  </Typography>

                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {"Yes: Allow the editing of the details."}
                          {`\n\nNo: Details will be not-editable, and will be filled according to Name, Requestor, and Language.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ py: 0.25 }}>
                      <FormControlLabel
                        control={<Switch sx={optionSwitchSx} />}
                        id={"AllowDetailsEditing"}
                        label="Edit Details Manually"
                        checked={allowDetailsEditing}
                        onChange={(event) => {
                          setAllowDetailsEditing(event.target.checked);

                          if (!event.target.checked)
                            fillDetails(
                              requestor.Name,
                              name,
                              Number(unitCount),
                              sourceLanguage?.Name || "",
                              targetLanguages,
                              translationType,
                            );
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                      >
                        Enable direct edits instead of generated details text.
                      </Typography>
                    </Box>
                  </HtmlTooltip>

                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Automatic calculation of Total Cost and Account Charge will be disabled."
                          }
                          {`\n\nNo: Automatic calculation of Total Cost and Account Charge will be enabled.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ py: 0.25 }}>
                      <FormControlLabel
                        control={<Switch sx={optionSwitchSx} />}
                        id={"disableAutomaticCalculationOfCharges"}
                        label="Disable Automatic Charge Calculation"
                        checked={disableAutomaticCalculationOfCharges}
                        onChange={(event) => {
                          setDisableAutomaticCalculationOfCharges(
                            event.target.checked,
                          );
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                      >
                        Manage cost and charge values manually for this service.
                      </Typography>
                    </Box>
                  </HtmlTooltip>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: "10px", height: "100%" }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Visibility & Creation
                  </Typography>

                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Show: Linguist Pay - Additional Linguist Cost - Additional Costs - Notarization/COT Cost - Delviery Cost - Payment Fees"
                          }
                          {`\n\nNo: Hide: Linguist Pay - Additional Linguist Cost - Additional Costs - Notarization/COT Cost - Delviery Cost - Payment Fees`}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ py: 0.25 }}>
                      <FormControlLabel
                        control={<Switch sx={optionSwitchSx} />}
                        id={"showLinguistPayAndOtherCosts"}
                        label="Show Linguist Pay & Other Costs"
                        checked={showLinguistPayAndOtherCosts}
                        onChange={(event) => {
                          setShowLinguistPayAndOtherCosts(event.target.checked);
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                      >
                        Reveal expanded cost fields for pay, fees, notarization, and delivery-related charges.
                      </Typography>
                    </Box>
                  </HtmlTooltip>

                  {isNewAssignment && targetLanguages.length > 1 ? (
                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Create one separate service for each target language. Each service will have its calculations based on a single language, with incrementing project numbers."
                            }
                            {`\n\nNo: Create one service with all target languages (current behavior).`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"createServicePerLanguage"}
                          label="Create One Service Per Language"
                          checked={createServicePerLanguage}
                          disabled={targetLanguages.length <= 1}
                          onChange={(event) => {
                            setCreateServicePerLanguage(event.target.checked);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                        >
                          Split multi-language requests into separate services with incrementing project numbers.
                        </Typography>
                      </Box>
                    </HtmlTooltip>
                  ) : null}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Overlay>

      {/* Paid Assignment Warning Dialog */}
      <Dialog
        open={showPaidWarning}
        onClose={handlePaidWarningCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            fontWeight: "bold",
          }}
        >
          ⚠️ Paid Assignment Warning
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText
            sx={{
              color: theme.palette.text.primary,
              whiteSpace: "pre-line",
              fontWeight: 500,
            }}
          >
            {paidWarningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handlePaidWarningCancel}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaidWarningConfirm}
            color="warning"
            variant="contained"
            sx={{ borderRadius: "8px" }}
            autoFocus
          >
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default TranslationNewEdit;
